import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { generateQRCodeWithLogo } from '@/utils/qrCodeWithLogo';
// Temporarily commented out for debugging
// import { useOnboarding } from '@/context/OnboardingContext';
// import OnboardingDashboard from '../OnboardingDashboard';
// import GuidedSetup from '../GuidedSetup';
import TierUpgradeModalSimple from '../TierUpgradeModalSimple';
import GuidedTour from '../common/GuidedTour';
import TierRestrictionBanner from '../TierRestrictionBanner';
import EnhancedDashboardView from './EnhancedDashboardView';
import ProfileCompletion from '../ProfileCompletion';
import { TailorMessagingPage } from './TailorMessagingPage';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { canAddCustomer, canAddKarigar, canAddOrder } from '../../config/tiers';
import AddCustomerModal from './AddCustomerModal';
import { 
  Users, 
  Package, 
  BarChart3,
  Search,
  X,
  MessageSquare,
  FileText,
  CreditCard,
  Settings,
  Plus,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Minus,
  Archive,
  Info,
  RefreshCw,
  Clock3,
  AlertCircle,
  Trash,
  Mail,
  ToggleLeft,
  ToggleRight,
  Wallet,
  DollarSign,
  Receipt
} from 'lucide-react';
import { EnhancedHeader } from '../common/EnhancedHeader';
import ReceiptsTab from '../shared/ReceiptsTab';
import PaymentMethodsModal from '../PaymentMethodsModal';

// Analytics Dashboard Component
interface AnalyticsDashboardProps {
  user: any;
  onNavigateToOrders: () => void;
  onNavigateToOverview: () => void;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ 
  user, 
  onNavigateToOrders, 
  onNavigateToOverview 
}) => {
  const { token } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [inventoryAnalytics, setInventoryAnalytics] = useState<any>(null);
  const [trendsData, setTrendsData] = useState<any>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [loading, setLoading] = useState(false); // Start as false, will be true when fetching
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  useEffect(() => {
    // Only fetch analytics data if user is authenticated
    if (user && token) {
      fetchAnalyticsData();
    }
  }, [timeRange, user, token]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch all analytics data in parallel
      const [statsResponse, inventoryResponse, trendsResponse, performanceResponse] = await Promise.all([
        fetch(`${API_BASE}/api/tailors/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/tailors/inventory/analytics/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/tailors/analytics/trends?period=${timeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/tailors/analytics/performance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setAnalyticsData(statsData);
      }

      if (inventoryResponse.ok) {
        const inventoryData = await inventoryResponse.json();
        setInventoryAnalytics(inventoryData);
      }

      if (trendsResponse.ok) {
        const trendsData = await trendsResponse.json();
        setTrendsData(trendsData);
      }

      if (performanceResponse.ok) {
        const performanceData = await performanceResponse.json();
        setPerformanceData(performanceData);
      }
    } catch (_error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getCompletionRate = () => {
    if (!analyticsData) return 0;
    const total = analyticsData.total_orders || 0;
    const completed = analyticsData.completed_orders || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getAverageOrderValue = () => {
    if (!analyticsData) return 0;
    const revenue = analyticsData.total_revenue || 0;
    const completed = analyticsData.completed_orders || 0;
    return completed > 0 ? revenue / completed : 0;
  };

  const getBusinessSuggestions = () => {
    const suggestions = [];
    
    if (analyticsData) {
      const completionRate = getCompletionRate();
      const avgOrderValue = getAverageOrderValue();
      
      if (completionRate < 80) {
        suggestions.push({
          type: 'warning',
          title: 'Improve Order Completion Rate',
          description: `Your completion rate is ${completionRate}%. Focus on timely delivery to increase customer satisfaction.`,
          action: 'Review pending orders'
        });
      }
      
      if (avgOrderValue < 5000) {
        suggestions.push({
          type: 'info',
          title: 'Increase Average Order Value',
          description: 'Consider offering premium services or upselling to increase revenue per order.',
          action: 'Add premium services'
        });
      }
      
      if (analyticsData.active_customers < 10) {
        suggestions.push({
          type: 'success',
          title: 'Expand Customer Base',
          description: 'You have a small customer base. Consider marketing strategies to attract more customers.',
          action: 'Marketing campaign'
        });
      }
    }
    
    if (inventoryAnalytics) {
      const lowStockPercentage = inventoryAnalytics.overview?.low_stock_percentage || 0;
      
      if (lowStockPercentage > 20) {
        suggestions.push({
          type: 'warning',
          title: 'High Low-Stock Items',
          description: `${lowStockPercentage}% of your inventory items are low on stock. Consider restocking.`,
          action: 'Restock inventory'
        });
      }
    }
    
    return suggestions.slice(0, 3); // Show top 3 suggestions
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  // Show message if user is not authenticated
  if (!user || !token) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600 mb-4">Please log in to view analytics data.</p>
          <button
            onClick={onNavigateToOverview}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Analytics & Reports</h2>
            <p className="text-gray-600 mt-1">Comprehensive business insights and performance metrics</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <button
              onClick={fetchAnalyticsData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(analyticsData?.total_revenue || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">From completed orders</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.total_orders || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {analyticsData?.completed_orders || 0} completed
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.active_customers || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Unique customers</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {getCompletionRate()}%
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Orders completed</p>
        </div>
      </div>

      {/* Business Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status Breakdown</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Completed</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData?.completed_orders || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Pending</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData?.pending_orders || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Total</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {analyticsData?.total_orders || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Average Order Value */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Order Value</h3>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(getAverageOrderValue())}
            </p>
            <p className="text-sm text-gray-500 mt-2">Per completed order</p>
          </div>
        </div>
      </div>

      {/* Inventory Analytics */}
      {inventoryAnalytics && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Inventory Analytics</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {inventoryAnalytics.overview?.total_items || 0}
              </p>
              <p className="text-sm text-gray-600">Total Items</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {inventoryAnalytics.overview?.low_stock_items || 0}
              </p>
              <p className="text-sm text-gray-600">Low Stock Items</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(inventoryAnalytics.overview?.total_value || 0)}
              </p>
              <p className="text-sm text-gray-600">Total Inventory Value</p>
            </div>
          </div>

          {/* Category Breakdown */}
          {inventoryAnalytics.category_breakdown && inventoryAnalytics.category_breakdown.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Category Breakdown</h4>
              <div className="space-y-3">
                {inventoryAnalytics.category_breakdown.slice(0, 5).map((category: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-900">{category.category}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">{category.item_count} items</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(category.category_value)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Score */}
      {performanceData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Business Performance Score</h3>
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-32 h-32">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke={performanceData.performance_score >= 80 ? "#10b981" : 
                         performanceData.performance_score >= 60 ? "#f59e0b" : "#ef4444"}
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${(performanceData.performance_score / 100) * 251.2} 251.2`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">
                  {performanceData.performance_score}
                </span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Efficiency Metrics</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Completion Rate</span>
                  <span className="text-sm font-medium">{performanceData.efficiency_metrics?.completion_rate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg Completion Time</span>
                  <span className="text-sm font-medium">{performanceData.efficiency_metrics?.avg_completion_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Orders This Month</span>
                  <span className="text-sm font-medium">{performanceData.efficiency_metrics?.orders_per_month}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Growth Metrics</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Customer Retention</span>
                  <span className="text-sm font-medium">{performanceData.growth_metrics?.customer_retention_rate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Repeat Customers</span>
                  <span className="text-sm font-medium">{performanceData.growth_metrics?.repeat_customers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Revenue This Month</span>
                  <span className="text-sm font-medium">{formatCurrency(performanceData.efficiency_metrics?.revenue_per_month || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trends Analysis */}
      {trendsData && trendsData.daily_trends && trendsData.daily_trends.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Trends Analysis ({timeRange})</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order Types */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Order Types Breakdown</h4>
              <div className="space-y-3">
                {trendsData.order_types?.slice(0, 5).map((type: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-900">{type.type || 'Unknown'}</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">{type.count} orders</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(type.revenue)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Insights */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Customer Insights</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Customers</span>
                  <span className="text-sm font-medium">{trendsData.customer_insights?.total_customers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">New Customers</span>
                  <span className="text-sm font-medium text-green-600">{trendsData.customer_insights?.new_customers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Repeat Customers</span>
                  <span className="text-sm font-medium text-blue-600">{trendsData.customer_insights?.repeat_customers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Retention Rate</span>
                  <span className="text-sm font-medium">{trendsData.customer_insights?.retention_rate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Business Suggestions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Business Growth Suggestions</h3>
        <div className="space-y-4">
          {/* Combine suggestions from multiple sources */}
          {[
            ...getBusinessSuggestions(),
            ...(performanceData?.recommendations || [])
          ].slice(0, 5).map((suggestion, index) => (
            <div key={index} className={`p-4 rounded-lg border-l-4 ${
              suggestion.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
              suggestion.type === 'info' ? 'bg-blue-50 border-blue-400' :
              'bg-green-50 border-green-400'
            }`}>
              <div className="flex items-start space-x-3">
                <div className={`p-1 rounded-full ${
                  suggestion.type === 'warning' ? 'bg-yellow-100' :
                  suggestion.type === 'info' ? 'bg-blue-100' :
                  'bg-green-100'
                }`}>
                  {suggestion.type === 'warning' ? (
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  ) : suggestion.type === 'info' ? (
                    <Info className="h-4 w-4 text-blue-600" />
                  ) : (
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{suggestion.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                  <div className="flex items-center justify-between mt-2">
                    <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                      {suggestion.action || 'Learn more'} →
                    </button>
                    {suggestion.priority && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        suggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                        suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {suggestion.priority}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};
import { DataTable, ColumnDef, OrderIdBadge } from '../shared';
import { formatCurrency } from '../../utils';
import { API_BASE, fetchWithFallback, FALLBACK_URLS } from '@/config/api';

interface Order {
  id: string;
  customerName: string;
  customer_phone?: string; // Add customer phone field
  customerId?: string | number;
  garmentType: string;
  status: string; // Can be 'new', 'pending', 'in_progress', 'completed', 'delivered', 'cancelled', 'paid', etc.
  dueDate: string;
  amount: number;
  priority: 'low' | 'medium' | 'high';
  order_number?: string;
  created_at?: string | null;
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  orderCount: number;
  lastOrderDate?: string | null;
  isActive?: boolean;
  is_verified_via_link?: boolean; // Indicates customer registered via tailor link
  linked_at?: string; // When customer linked their account
}

interface RealInventoryItem {
  id: number;
  name: string;
  description?: string;
  sku?: string;
  category: string;
  subcategory?: string;
  supplier?: {
    id: number;
    company_name: string;
  };
  current_stock: number;
  minimum_stock: number;
  maximum_stock?: number;
  unit: string;
  unit_weight?: number;
  unit_dimensions?: string;
  cost_per_unit?: number;
  selling_price?: number;
  storage_location?: string;
  storage_conditions?: string;
  image_url?: string;
  color?: string;
  is_active: boolean;
  low_stock_alert_sent: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  low_stock: boolean;
  total_value: number;
}

interface InventoryStats {
  total_items: number;
  total_stock: number;
  low_stock_items: number;
  total_value: number;
}

interface Supplier {
  id: number;
  company_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country: string;
  payment_terms?: string;
  is_active: boolean;
  total_items: number;
}

type ModalMode = 'create' | 'edit';

type QuickAddData = {
  name: string;
  quantity: string;
  unit: string;
  category: string;
};

type AdvancedData = {
  description: string;
  sku: string;
  subcategory: string;
  supplier_id: string;
  minimum_stock: string;
  maximum_stock: string;
  cost_per_unit: string;
  selling_price: string;
  color: string;
  notes: string;
};

const DEFAULT_MIN_STOCK = 5;

const QUICK_ADD_DEFAULT: QuickAddData = {
  name: '',
  quantity: '',
  unit: 'pieces',
  category: ''
};

const ADVANCED_DEFAULT: AdvancedData = {
  description: '',
  sku: '',
  subcategory: '',
  supplier_id: '',
  minimum_stock: String(DEFAULT_MIN_STOCK),
  maximum_stock: '',
  cost_per_unit: '',
  selling_price: '',
  color: '',
  notes: ''
};

type StatTone = 'info' | 'success' | 'warning' | 'neutral' | 'danger';

interface StatSummary {
  id: string;
  label: string;
  value: string;
  icon: React.ElementType;
  description?: string;
  tone?: StatTone;
}

const CARD_BASE = 'bg-white border border-gray-200 rounded-xl shadow-sm';

const statToneStyles: Record<StatTone, string> = {
  info: 'bg-blue-100 text-blue-600',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-orange-100 text-orange-600',
  neutral: 'bg-gray-100 text-gray-500',
  danger: 'bg-red-100 text-red-600'
};

const StatCard: React.FC<{ stat: StatSummary }> = ({ stat }) => {
  const Icon = stat.icon;

  return (
    <div className={`${CARD_BASE} p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{stat.label}</p>
          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
        </div>
        <div className={`p-3 rounded-lg ${statToneStyles[stat.tone ?? 'info']}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {stat.description ? (
        <p className="text-xs text-gray-500 mt-2">{stat.description}</p>
      ) : null}
    </div>
  );
};

interface SectionHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, description, actions }) => (
  <div className={`${CARD_BASE} p-6`}>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {description ? <p className="text-gray-600 mt-1">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center space-x-3">{actions}</div> : null}
    </div>
  </div>
);

const BusinessDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { totalUnread } = useUnreadMessages();
  // const { startOnboarding } = useOnboarding(); // Temporarily commented out
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from localStorage or default to 'overview'
    return localStorage.getItem('businessDashboardActiveTab') || 'overview';
  });

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('businessDashboardActiveTab', activeTab);
  }, [activeTab]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateMessage, setTemplateMessage] = useState('');
  const [showGuidedSetup, setShowGuidedSetup] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState('classic');
  const [showTierUpgrade, setShowTierUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'customer' | 'karigar' | 'order' | 'general'>('general');
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  
  // Dashboard view state
  const [dashboardView, setDashboardView] = useState<'classic' | 'enhanced'>(() => {
    const saved = localStorage.getItem('businessDashboardView');
    return (saved === 'classic' || saved === 'enhanced') ? saved : 'classic';
  });
  
  // Save dashboard view preference to localStorage
  useEffect(() => {
    localStorage.setItem('businessDashboardView', dashboardView);
  }, [dashboardView]);
  
  const toggleDashboardView = () => {
    setDashboardView(prev => prev === 'classic' ? 'enhanced' : 'classic');
  };
  
  // Profile completion handler
  const handleProfileStepClick = (stepId: string) => {
    switch (stepId) {
      case 'basic_info':
        setActiveTab('profile');
        break;
      case 'business_details':
        setActiveTab('profile');
        break;
      case 'profile_photo':
        setActiveTab('profile');
        break;
      case 'verification':
        // Navigate to verification page or show verification modal
        alert('Verification feature coming soon!');
        break;
      case 'portfolio':
        // Navigate to portfolio management
        alert('Portfolio management coming soon!');
        break;
      case 'preferences':
        setActiveTab('profile');
        break;
      default:
        console.log('Unknown step:', stepId);
    }
  };
  
  // Real data from API
  const [userTier, setUserTier] = useState('free');
  
  // Update user tier from user data
  useEffect(() => {
    console.log('🔍 User object:', user);
    console.log('🔍 User tier:', user?.tier);
    console.log('🔍 User email:', user?.email);
    
    if (user?.tier) {
      console.log('✅ Setting tier from user.tier:', user.tier);
      setUserTier(user.tier);
    } else {
      console.log('⚠️ No tier found in user object, defaulting to free');
      setUserTier('free');
    }
  }, [user]);
  const [currentCounts, setCurrentCounts] = useState({
    customers: 0,
    karigars: 0,
    orders: 0
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isCustomerDetailOpen, setIsCustomerDetailOpen] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  
  // Order filter states
  const [orderStatusFilter, setOrderStatusFilter] = useState<string>('all');
  const [orderPriorityFilter, setOrderPriorityFilter] = useState<string>('all');

  // Filtered customers based on search term
  const filteredCustomers = React.useMemo(() => {
    if (!customerSearchTerm.trim()) return customers;
    
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      customer.name.toLowerCase().includes(searchLower) ||
      customer.email.toLowerCase().includes(searchLower) ||
      (customer.phone && customer.phone.includes(searchLower))
    );
  }, [customers, customerSearchTerm]);

  // Customer management functions
  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsCustomerDetailOpen(true);
  };

  const handleSendReminder = async (customer: Customer) => {
    try {
      // TODO: Implement actual reminder sending
      console.log('Sending reminder to:', customer.email);
      setInventorySuccessMessage(`Reminder sent to ${customer.name}`);
      setTimeout(() => setInventorySuccessMessage(''), 3000);
    } catch (_error) {
      console.error('Failed to send reminder:', error);
      setInventoryError('Failed to send reminder. Please try again.');
    }
  };

  const handleResendInvitation = async (customer: Customer) => {
    try {
      // TODO: Implement actual invitation resending
      console.log('Resending invitation to:', customer.email);
      setInventorySuccessMessage(`Invitation resent to ${customer.name}`);
      setTimeout(() => setInventorySuccessMessage(''), 3000);
    } catch (_error) {
      console.error('Failed to resend invitation:', error);
      setInventoryError('Failed to resend invitation. Please try again.');
    }
  };

  const [inventory, setInventory] = useState<RealInventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryStats, setInventoryStats] = useState<InventoryStats>({
    total_items: 0,
    total_stock: 0,
    low_stock_items: 0,
    total_value: 0
  });
  const [archivedInventoryCount, setArchivedInventoryCount] = useState(0);
  
  // Inventory search and filtering states
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [inventoryFilterCategory, setInventoryFilterCategory] = useState('');
  const [inventorySortBy, setInventorySortBy] = useState<'name' | 'stock' | 'category' | 'created_at'>('name');
  const [inventorySortOrder, setInventorySortOrder] = useState<'asc' | 'desc'>('asc');
  const [showArchivedItems, setShowArchivedItems] = useState(false);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  
  // Enhanced inventory state
  const [isInventorySubmitting, setIsInventorySubmitting] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [inventorySuccessMessage, setInventorySuccessMessage] = useState('');
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [quickAddData, setQuickAddData] = useState<QuickAddData>(QUICK_ADD_DEFAULT);
  const [advancedData, setAdvancedData] = useState<AdvancedData>(ADVANCED_DEFAULT);
  const [modalError, setModalError] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<RealInventoryItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [showStockRules, setShowStockRules] = useState(false);
  
  // Customer creation modal state
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerModalMode, setCustomerModalMode] = useState<'select' | 'new' | 'link' | 'code'>('select');
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      coordinates: undefined
    }
  });
  const [linkingData, setLinkingData] = useState({
    linkUrl: '',
    qrCodeData: '',
    qrCodeImage: '',
    expiresAt: '',
    isGenerating: false
  });
  const [linkingCodeData, setLinkingCodeData] = useState({
    code: '',
    qrCodeImage: '',
    expiresAt: '',
    isGenerating: false
  });
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [customerSuccessMessage, setCustomerSuccessMessage] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');

  // Common country codes for phone numbers
  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
    { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
    { code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+977', country: 'Nepal', flag: '🇳🇵' },
    { code: '+975', country: 'Bhutan', flag: '🇧🇹' }
  ];
  
  // Archive confirmation modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [itemToArchive, setItemToArchive] = useState<RealInventoryItem | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<RealInventoryItem | null>(null);

  const buildAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const cloneRequestInit = (init?: RequestInit): RequestInit | undefined => {
    if (!init) return undefined;
    const cloned: RequestInit = { ...init };
    if (init.headers instanceof Headers) {
      cloned.headers = new Headers(init.headers);
    } else if (Array.isArray(init.headers)) {
      cloned.headers = [...init.headers];
    } else if (init.headers) {
      cloned.headers = { ...(init.headers as Record<string, string>) };
    }
    if (typeof init.body !== 'undefined') {
      cloned.body = init.body;
    }
    return cloned;
  };

  const buildInventoryCandidates = (path: string): string[] => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const candidates = [`/api/tailors${normalized}`];

    if (normalized.startsWith('/inventory')) {
      const suffix = normalized.substring('/inventory'.length);
      candidates.push(`/api/inventory${suffix}`);
    } else {
      candidates.push(`/api${normalized}`);
    }

    return Array.from(new Set(candidates));
  };

  const inventoryFetch = async (path: string, init?: RequestInit): Promise<Response> => {
    console.log('🔍 DEBUG: inventoryFetch called with path:', path);
    console.log('🔍 DEBUG: FALLBACK_URLS:', FALLBACK_URLS);
    
    const candidates = buildInventoryCandidates(path);
    console.log('🔍 DEBUG: Built candidates:', candidates);
    let lastResponse: Response | null = null;

    for (const candidate of candidates) {
      try {
        console.log('🔍 DEBUG: Trying candidate:', candidate);
        // Use fallback fetch for better reliability
        const response = await fetchWithFallback(candidate, cloneRequestInit(init));
        console.log('🔍 DEBUG: Response status:', response.status);
        if (response.status !== 404) {
          if (!candidate.startsWith('/api/tailors')) {
            console.info('[inventory] Using fallback endpoint:', candidate);
          }
          return response;
        }
        lastResponse = response;
      } catch (_error) {
        console.warn(`[inventory] Failed to fetch ${candidate}:`, error);
        // Continue to next candidate
      }
    }

    return lastResponse ?? new Response(null, { status: 404 });
  };

  const emptyInventoryStats = (): InventoryStats => ({
    total_items: 0,
    total_stock: 0,
    low_stock_items: 0,
    total_value: 0
  });

  const numberFormatter = useMemo(() => new Intl.NumberFormat('en-IN'), []);
  const cardClasses = CARD_BASE;

  const orderStats = useMemo<StatSummary[]>(() => {
    const total = orders.length;
    const inProduction = orders.filter(order => order.status === 'new' || order.status === 'in_progress').length;
    const fulfilled = orders.filter(order => order.status === 'completed' || order.status === 'delivered').length;
    const delivered = orders.filter(order => order.status === 'delivered').length;
    const pastDue = orders.filter(order => {
      if (!order.dueDate) return false;
      const dueDate = new Date(order.dueDate);
      if (Number.isNaN(dueDate.getTime())) return false;
      return dueDate < new Date() && !['completed', 'delivered', 'cancelled'].includes(order.status);
    }).length;

    return [
      {
        id: 'orders-total',
        label: 'Total Orders',
        value: numberFormatter.format(total),
        icon: Package,
        description: `${numberFormatter.format(inProduction)} active`,
        tone: 'info'
      },
      {
        id: 'orders-active',
        label: 'In Production',
        value: numberFormatter.format(inProduction),
        icon: Clock,
        description: pastDue ? `${numberFormatter.format(pastDue)} overdue` : 'On schedule',
        tone: 'warning'
      },
      {
        id: 'orders-fulfilled',
        label: 'Fulfilled',
        value: numberFormatter.format(fulfilled),
        icon: CheckCircle,
        description: delivered ? `${numberFormatter.format(delivered)} delivered` : 'Awaiting pickup',
        tone: 'success'
      },
      {
        id: 'orders-past-due',
        label: 'Orders Past Due',
        value: numberFormatter.format(pastDue),
        icon: AlertCircle,
        description: pastDue ? 'Requires immediate attention' : 'All orders on time',
        tone: 'danger'
      }
    ];
  }, [orders, numberFormatter]);

  const customerStats = useMemo<StatSummary[]>(() => {
    const total = customers.length;
    // Engaged customers are those who have actually placed orders
    const engaged = customers.filter(customer => (customer.orderCount ?? 0) > 0).length;
    const returning = customers.filter(customer => (customer.orderCount ?? 0) > 1).length;
    // Pending customers are those who haven't placed any orders yet
    const pending = customers.filter(customer => (customer.orderCount ?? 0) === 0).length;
    const totalOrdersFromCustomers = customers.reduce((sum, customer) => sum + (customer.orderCount ?? 0), 0);
    const averageOrders = engaged > 0 ? (totalOrdersFromCustomers / engaged).toFixed(1) : '0.0';

    return [
      {
        id: 'customers-total',
        label: 'Total Customers',
        value: numberFormatter.format(total),
        icon: Users,
        description: `${numberFormatter.format(total)} total`,
        tone: 'info'
      },
      {
        id: 'customers-engaged',
        label: 'Engaged Customers',
        value: numberFormatter.format(engaged),
        icon: CheckCircle,
        description: engaged > 0 ? `${averageOrders} orders/customer` : 'No orders placed yet',
        tone: 'success'
      },
      {
        id: 'customers-pending',
        label: 'Pending Invitations',
        value: numberFormatter.format(pending),
        icon: Mail,
        description: pending > 0 ? 'Waiting for first order' : 'All customers have ordered',
        tone: 'warning'
      },
      {
        id: 'customers-repeat',
        label: 'Repeat Buyers',
        value: numberFormatter.format(returning),
        icon: BarChart3,
        description: 'Placed 2+ orders',
        tone: 'neutral'
      }
    ];
  }, [customers, numberFormatter]);

  const refreshInventoryData = async (): Promise<void> => {
    if (!token) {
      setInventory([]);
      setInventoryStats(emptyInventoryStats());
      setArchivedInventoryCount(0);
      return;
    }

    try {
      console.log('🔄 Refreshing inventory data...');
      const response = await inventoryFetch('/inventory', { headers: buildAuthHeaders() });

      if (response.ok) {
        const inventoryData = await response.json();
        console.log('🔍 DEBUG: Raw inventory response:', inventoryData);
        const items: RealInventoryItem[] = inventoryData.items || [];
        const activeItems = items.filter(item => item.is_active !== false);
        
        console.log('🔄 Inventory refresh - Total items:', items.length, 'Active items:', activeItems.length);
        console.log('🔄 Inventory items:', items.map(item => ({ id: item.id, name: item.name, is_active: item.is_active })));
        
        setInventory(items);
        setInventoryStats({
          total_items: activeItems.length,
          total_stock: activeItems.reduce((sum, item) => sum + item.current_stock, 0),
          low_stock_items: activeItems.filter(item => item.current_stock <= item.minimum_stock).length,
          total_value: activeItems.reduce((sum, item) => sum + (item.cost_per_unit || 0) * item.current_stock, 0)
        });
        setArchivedInventoryCount(items.length - activeItems.length);
        
        console.log('🔄 Updated inventory stats:', {
          total_items: activeItems.length,
          total_stock: activeItems.reduce((sum, item) => sum + item.current_stock, 0),
          low_stock_items: activeItems.filter(item => item.current_stock <= item.minimum_stock).length,
          total_value: activeItems.reduce((sum, item) => sum + (item.cost_per_unit || 0) * item.current_stock, 0)
        });
      } else if (response.status === 404) {
        console.info('[inventory] Endpoint returned 404; defaulting to empty inventory state');
        setInventory([]);
        setInventoryStats(emptyInventoryStats());
        setArchivedInventoryCount(0);
      } else {
        console.error('Failed to load inventory:', response.status);
        setInventory([]);
        setInventoryStats(emptyInventoryStats());
        setArchivedInventoryCount(0);
      }
    } catch (_error) {
      console.error('Failed to load inventory:', error);
      setInventory([]);
      setInventoryStats(emptyInventoryStats());
      setArchivedInventoryCount(0);
    }
  };

  // First-time-only auto-launch tour for tailors
  useEffect(() => {
    try {
      if (!localStorage.getItem('tour.tailor.shown')) {
        setShowGuidedSetup(true);
      }
    } catch (_) {}
  }, []);

  // Real stats from API
  const [stats, setStats] = useState([
    { title: 'Total Orders', value: '0', change: '+0%', trend: 'up', icon: Package },
    { title: 'Active Customers', value: '0', change: '+0%', trend: 'up', icon: Users },
    { title: 'Monthly Revenue', value: formatCurrency(0), change: '+0%', trend: 'up', icon: CreditCard },
    { title: 'Pending Orders', value: '0', change: '+0%', trend: 'down', icon: Clock },
  ]);

  const handleTemplateSelect = async (template: string) => {
    if (!user || !token) return;

    setIsSavingTemplate(true);
    setTemplateMessage('');

    try {
      const apiUrl = API_BASE;

      if (!token) {
        setTemplateMessage('Authentication token not found. Please log in again.');
        return;
      }

      // Get current profile data first
      const getResponse = await fetch(`${apiUrl}/api/tailors/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let currentProfile = {};
      if (getResponse.ok) {
        currentProfile = await getResponse.json();
      }

      // Update with new template
      const updateResponse = await fetch(`${apiUrl}/api/tailors/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...currentProfile,
          template_style: template,
          custom_colors: getDefaultColors(template),
          layout_preferences: getDefaultLayout(template)
        })
      });

      if (updateResponse.ok) {
        setCurrentTemplate(template);
        setTemplateMessage(`Template changed to ${template}! Your public profile has been updated.`);
        
        // Determine slug from existing profile or refetch once if missing
        let slug = (currentProfile as any)?.slug;
        try {
          if (!slug) {
            const refetch = await fetch(`${apiUrl}/api/tailors/profile`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (refetch.ok) {
              const refreshed = await refetch.json();
              slug = refreshed?.slug;
            }
          }
        } catch (_) {}

        if (slug) {
          const publicProfileUrl = `${window.location.origin}/t/${slug}`;
          const timestamp = Date.now();
          const cacheBustedUrl = `${publicProfileUrl}?refresh=${timestamp}`;
          window.open(cacheBustedUrl, '_blank');
        }
      } else {
        setTemplateMessage('Failed to update template. Please try again.');
      }
    } catch (_error) {
      console.error('Error updating template:', error);
      setTemplateMessage('Failed to update template. Please check your connection.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const getDefaultColors = (template: string) => {
    const colors = {
      classic: {
        primary: '#8B4513',
        secondary: '#D2691E',
        accent: '#FFD700',
        background: '#F5F5DC',
        text: '#2F2F2F'
      },
      modern: {
        primary: '#1E40AF',
        secondary: '#64748B',
        accent: '#F59E0B',
        background: '#F8FAFC',
        text: '#1E293B'
      },
      minimal: {
        primary: '#000000',
        secondary: '#333333',
        accent: '#666666',
        background: '#FFFFFF',
        text: '#000000'
      }
    };
    return colors[template as keyof typeof colors] || colors.classic;
  };

  const getDefaultLayout = (template: string) => {
    return {
      heroStyle: 'large',
      photoLayout: 'grid',
      contentOrder: 'hero-info-portfolio',
      sidebar: true
    };
  };

  // Helper function to calculate days until due or past due
  const getDaysUntilDue = useCallback((dueDate: string, status: string): number | null => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    if (Number.isNaN(due.getTime())) return null;
    
    // If order is completed or delivered, return null (will be sorted to bottom)
    if (['completed', 'delivered'].includes(status)) return null;
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  // Helper function to format days until due
  const formatDaysUntilDue = useCallback((dueDate: string, status: string): string => {
    const days = getDaysUntilDue(dueDate, status);
    if (days === null) {
      if (['completed', 'delivered'].includes(status)) {
        return 'Completed';
      }
      return 'No due date';
    }
    
    if (days < 0) {
      return `${Math.abs(days)} days overdue`;
    } else if (days === 0) {
      return 'Due today';
    } else {
      return `${days} days until due`;
    }
  }, [getDaysUntilDue]);

  // Order table columns
  const orderColumns: ColumnDef<Order>[] = [
    {
      key: 'id',
      header: 'Order',
      searchable: true,
      searchPlaceholder: 'Search order ID...',
      render: (value, row) => (
        <OrderIdBadge orderNumber={(row as any).order_number} id={value} compact />
      )
    },
    {
      key: 'customerName',
      header: 'Customer',
      searchable: true,
      searchPlaceholder: 'Search customer name...'
    },
    {
      key: 'customer_phone',
      header: 'Phone Number',
      searchable: true,
      searchPlaceholder: 'Search phone number...',
      render: (value) => value || 'N/A'
    },
    {
      key: 'dueDate',
      header: 'Days Until Due',
      searchable: true,
      searchPlaceholder: 'Search date...',
      render: (value, row) => {
        const days = getDaysUntilDue(value, row.status);
        const isOverdue = days !== null && days < 0;
        const isDueToday = days === 0;
        const isCompleted = ['completed', 'delivered'].includes(row.status);
        
        return (
          <span className={
            isCompleted 
              ? 'text-gray-500' 
              : isOverdue 
                ? 'text-red-600 font-semibold' 
                : isDueToday 
                  ? 'text-orange-600 font-semibold' 
                  : 'text-gray-900'
          }>
            {formatDaysUntilDue(value, row.status)}
          </span>
        );
      },
      sortable: true
    }
  ];

  const orderActions = [
    { label: 'Manage', action: 'manage', variant: 'primary' as const },
    { label: 'View Customer', action: 'customer', variant: 'secondary' as const },
  ];

  // No filtering needed - column-level search handles filtering
  const filteredOrders = useMemo(() => {
    return orders;
  }, [orders]);

  // Sort orders: completed/delivered to bottom, then by days until due
  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      const aCompleted = ['completed', 'delivered'].includes(a.status);
      const bCompleted = ['completed', 'delivered'].includes(b.status);
      
      // Move completed orders to bottom
      if (aCompleted && !bCompleted) return 1;
      if (!aCompleted && bCompleted) return -1;
      if (aCompleted && bCompleted) return 0; // Both completed, keep order
      
      // For non-completed orders, sort by days until due
      const aDays = getDaysUntilDue(a.dueDate, a.status);
      const bDays = getDaysUntilDue(b.dueDate, b.status);
      
      if (aDays === null && bDays === null) return 0;
      if (aDays === null) return 1; // No due date goes to bottom
      if (bDays === null) return -1;
      
      // Sort by days (overdue/soonest first)
      return aDays - bDays;
    });
  }, [filteredOrders, getDaysUntilDue]);

  // Helper to get row className for highlighting overdue orders
  const getOrderRowClassName = (row: Order): string => {
    const days = getDaysUntilDue(row.dueDate, row.status);
    const isOverdue = days !== null && days < 0;
    
    if (isOverdue) {
      return 'bg-red-50 border-l-4 border-red-500';
    }
    
    const isDueToday = days === 0;
    if (isDueToday) {
      return 'bg-orange-50 border-l-4 border-orange-500';
    }
    
    return '';
  };

  const handleOrderAction = (action: string, row: Order) => {
    switch (action) {
      case 'manage': {
        const orderNumber = (row as any).order_number || row.id;
        navigate(`/order/${orderNumber}/details`);
        break;
      }
      case 'customer':
        if ((row as any).customerId) {
          navigate(`/business/customers/${(row as any).customerId}`);
        } else {
          console.warn('No customerId on order row; cannot navigate to customer.', row);
          alert('Unable to view customer details: Customer information is not available for this order. Please contact support if this issue persists.');
        }
        break;
    }
  };

  const handleAddCustomer = () => {
    if (!canAddCustomer(currentCounts.customers, userTier)) {
      setUpgradeReason('customer');
      setShowTierUpgrade(true);
      return;
    }
    // Clear any previous errors and open customer creation modal
    setCustomerError('');
    setCustomerSuccessMessage('');
    setCustomerModalMode('select');
    setIsCustomerModalOpen(true);
  };

  const handleGenerateLinkingCode = async () => {
    setLinkingCodeData(prev => ({ ...prev, isGenerating: true }));
    setCustomerError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/tailors/customers/generate-code`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        
        // Parse QR data
        let qrDataString = data.qr_data;
        try {
          qrDataString = JSON.parse(data.qr_data);
        } catch {
          // Already a string or object
        }
        
        // Generate QR code image with MasterJi logo
        const qrCodeImage = await generateQRCodeWithLogo(data.qr_data, {
          width: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          logoSizeRatio: 0.2
        });
        
        setLinkingCodeData({
          code: data.code,
          qrCodeImage: qrCodeImage,
          expiresAt: data.expires_at,
          isGenerating: false
        });
        setCustomerModalMode('code');
      } else {
        const errorData = await response.json();
        setCustomerError(`Failed to generate code: ${errorData.detail || 'Unknown error'}`);
        setLinkingCodeData(prev => ({ ...prev, isGenerating: false }));
      }
    } catch (_error) {
      console.error('Error generating linking code:', error);
      setCustomerError('Network error occurred while generating code');
      setLinkingCodeData(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleGenerateLinkingLink = async () => {
    setLinkingData(prev => ({ ...prev, isGenerating: true }));
    
    try {
      const response = await fetch(`${API_BASE}/api/tailors/customers/generate-link`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({
          tailor_id: user?.id,
          tailor_name: user?.name || user?.email,
          expires_in_hours: 24
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Generate QR code image with MasterJi logo
        const qrCodeImage = await generateQRCodeWithLogo(data.link_url, {
          width: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          logoSizeRatio: 0.2
        });
        
        setLinkingData({
          linkUrl: data.link_url,
          qrCodeData: data.qr_code_data,
          qrCodeImage: qrCodeImage,
          expiresAt: data.expires_at,
          isGenerating: false
        });
        setCustomerModalMode('link');
      } else {
        const errorData = await response.json();
        setCustomerError(`Failed to generate link: ${errorData.detail || 'Unknown error'}`);
        setLinkingData(prev => ({ ...prev, isGenerating: false }));
      }
    } catch (_error) {
      console.error('Error generating linking link:', error);
      setCustomerError('Network error occurred while generating link');
      setLinkingData(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleAddKarigar = () => {
    if (!canAddKarigar(currentCounts.karigars, userTier)) {
      setUpgradeReason('karigar');
      setShowTierUpgrade(true);
      return;
    }
    // Proceed with adding karigar
    console.log('Adding new karigar...');
  };

  const handleCreateCustomer = async () => {
    if (!token || !customerFormData.name.trim()) {
      return;
    }

    setIsCreatingCustomer(true);
    try {
      // Generate a temporary email if none provided
      const email = customerFormData.email.trim() || `${customerFormData.name.toLowerCase().replace(' ', '.')}@temp.masterji.com`;
      
      const requestData = {
        name: customerFormData.name.trim(),
        email: email,
        password: 'TempPass123!', // Temporary password - customer must change on first login
        role: 'customer',
        phone: customerFormData.phone.trim() ? `${selectedCountryCode}${customerFormData.phone.trim()}` : null
      };
      
      console.log('Creating customer with data:', requestData);
      
      const response = await fetch(`${API_BASE}/api/register`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const newCustomer = await response.json();
        console.log('Customer created successfully:', newCustomer);
        
        // Create customer object for local state
        const generatedId = newCustomer?.user?.id ?? newCustomer?.id;
        const normalizedCustomer: Customer = {
          id: generatedId ? String(generatedId) : `temp-${Date.now()}`,
          name: customerFormData.name.trim(),
          email,
          phone: customerFormData.phone.trim() ? `${selectedCountryCode}${customerFormData.phone.trim()}` : null,
          orderCount: 0,
          lastOrderDate: null,
          isActive: true,
          is_verified_via_link: false, // New customers are not verified yet
          linked_at: null
        };

        let customerAlreadyExists = false;
        setCustomers((prev) => {
          const hasExisting = prev.some((customer) => customer.id === normalizedCustomer.id);
          customerAlreadyExists = hasExisting;
          if (hasExisting) {
            return prev.map((customer) =>
              customer.id === normalizedCustomer.id ? { ...customer, ...normalizedCustomer } : customer
            );
          }
          return [normalizedCustomer, ...prev];
        });

        if (!customerAlreadyExists) {
          setCurrentCounts((prev) => ({
            ...prev,
            customers: prev.customers + 1,
          }));
        }
        
        // Close modal and reset form
        setIsCustomerModalOpen(false);
        setCustomerFormData({ 
          name: '', 
          email: '', 
          phone: '', 
          address: {
            address: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            coordinates: undefined
          }
        });
        setSelectedCountryCode('+91');
        
        // Send invitation email/SMS with temporary password
        await sendCustomerInvitation(newCustomer, email, 'TempPass123!');
        
        // Refresh customers list
        setCustomerSuccessMessage('Customer created and invitation sent successfully!');
        setTimeout(() => setCustomerSuccessMessage(''), 5000);
        
        // Refresh dashboard data to show new customer
        await refreshInventoryData();
      } else {
        const errorData = await response.json();
        console.error('Failed to create customer:', response.status, errorData);
        console.error('Request data that failed:', requestData);
        console.error('Response headers:', response.headers);
        
        // Handle specific error cases
        let errorMessage = 'Failed to create customer. Please try again.';
        
        if (errorData.error?.message === 'User with this email already exists') {
          errorMessage = `A user with email "${email}" already exists. Please use a different email address.`;
        } else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        setCustomerError(errorMessage);
      }
    } catch (_error) {
      console.error('Error creating customer:', error);
      setCustomerError('Failed to create customer. Please check your connection.');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (!confirm('Are you sure you want to remove this customer from your customer list? This will unlink them from your business but they will remain in the system.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/tailors/customers/${customerId}`, {
        method: 'DELETE',
        headers: buildAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Customer unlinked:', result);
        
        // Remove customer from local state immediately (optimistic update)
        setCustomers(prevCustomers => 
          prevCustomers.filter(customer => customer.id !== customerId)
        );
        
        // Update customer count
        setCurrentCounts(prev => ({
          ...prev,
          customers: prev.customers - 1
        }));
        
        setCustomerSuccessMessage('Customer removed from your list successfully!');
        setTimeout(() => setCustomerSuccessMessage(''), 5000);
        
        // No need to reload the page - we've updated the state
      } else {
        const errorData = await response.json();
        setCustomerError(`Failed to remove customer: ${errorData.detail || errorData.message || 'Please try again'}`);
      }
    } catch (_error) {
      console.error('Error unlinking customer:', error);
      setCustomerError('Failed to remove customer. Please check your connection.');
    }
  };

  const handleAddOrder = () => {
    if (!canAddOrder(currentCounts.orders, userTier)) {
      setUpgradeReason('order');
      setShowTierUpgrade(true);
      return;
    }
    // Navigate to new order form
    navigate('/business/orders/new');
  };

  const handleTierUpgrade = (tierId: string) => {
    console.log(`Upgrading to ${tierId} tier...`);
    setShowTierUpgrade(false);
    // In real app, this would call the payment API
  };

  const getMostRecentDate = (first?: string | null, second?: string | null): string | null => {
    if (!first) return second ?? null;
    if (!second) return first ?? null;

    const firstDate = new Date(first);
    const secondDate = new Date(second);
    if (Number.isNaN(firstDate.getTime())) return second;
    if (Number.isNaN(secondDate.getTime())) return first;
    return firstDate > secondDate ? first : second;
  };

  const mergeCustomerLists = (existingCustomers: Customer[], incomingCustomers: Customer[]): Customer[] => {
    const customersById = new Map<string, Customer>();

    existingCustomers.forEach((customer) => {
      customersById.set(String(customer.id), customer);
    });

    incomingCustomers.forEach((customer) => {
      const key = String(customer.id);
      const existing = customersById.get(key);

      if (existing) {
        customersById.set(key, {
          ...existing,
          ...customer,
          orderCount: Math.max(existing.orderCount ?? 0, customer.orderCount ?? 0),
          lastOrderDate: getMostRecentDate(existing.lastOrderDate ?? null, customer.lastOrderDate ?? null),
          phone: customer.phone ?? existing.phone ?? null,
          email: customer.email || existing.email,
        });
      } else {
        customersById.set(key, customer);
      }
    });

    return Array.from(customersById.values());
  };

  // Load all dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      // Wait for authentication to complete
      if (!token || !user) return;

      try {
        // Load stats by calculating from orders (like TailorDashboard does)
        setIsLoadingStats(true);
        
        // Debug logging
        console.log('?? Debug - API_BASE:', API_BASE);
        console.log('?? Debug - Token:', token ? `${token.substring(0, 20)}...` : 'null');
        console.log('?? Debug - User:', user);
        console.log('?? Debug - Full Token:', token);
        console.log('?? Debug - User Role:', user?.role);
        
        const ordersResp = await fetch(`${API_BASE}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (ordersResp.ok) {
          const ordersData = await ordersResp.json();
          const orders = ordersData.orders || [];
          
          // Stats will be calculated by useEffect when orders state is updated
          // Just set currentCounts here
          setCurrentCounts({
            customers: new Set(orders.map((o: any) => o.customer_id || o.customer?.id)).size,
            karigars: 0,
            orders: orders.length
          });
        }
      } catch (_e) {
        console.error('Failed to load stats:', e);
      } finally {
        setIsLoadingStats(false);
      }

      try {
        // Load customers by extracting from orders data and augmenting with any available APIs
        setIsLoadingCustomers(true);
        const ordersResp = await fetch(`${API_BASE}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (ordersResp.ok) {
          const ordersData = await ordersResp.json();
          const orders = ordersData.orders || [];

          // Build customer list from orders (covers most data sets)
          const customerMap = new Map<string, Customer>();
          orders.forEach((order: any) => {
            const rawCustomerId = order.customer_id || order.customer?.id;
            if (!rawCustomerId) {
              return;
            }

            const customerId = String(rawCustomerId);
            const orderDate: string | null = order.created_at || order.order_date || null;
            const existing = customerMap.get(customerId);

            if (existing) {
              customerMap.set(customerId, {
                ...existing,
                orderCount: (existing.orderCount ?? 0) + 1,
                lastOrderDate: getMostRecentDate(existing.lastOrderDate ?? null, orderDate),
                email: existing.email || order.customer?.email || '',
                phone: existing.phone ?? order.customer?.phone ?? null,
              });
              return;
            }

            customerMap.set(customerId, {
              id: customerId,
              name: order.customer_name || order.customer?.name || 'Unknown Customer',
              email: order.customer?.email || '',
              phone: order.customer?.phone || null,
              orderCount: 1,
              lastOrderDate: orderDate,
              isActive: order.customer?.is_active ?? true,
            });
          });

          let aggregatedCustomers = Array.from(customerMap.values());

          // Tailor dashboards have a dedicated endpoint that includes customers without orders.
          if (user?.role === 'tailor') {
            try {
              const customersResponse = await fetch(`${API_BASE}/api/tailors/customers`, {
                headers: buildAuthHeaders()
              });

              if (customersResponse.ok) {
                const customersData = await customersResponse.json();
                const customersFromAPI = customersData.customers || [];
                const convertedCustomers: Customer[] = customersFromAPI.map((customer: any) => ({
                  id: String(customer.id),
                  name: customer.name || 'Unknown Customer',
                  email: customer.email || '',
                  phone: customer.phone || null,
                  orderCount: customer.order_count || 0,
                  lastOrderDate: customer.last_order_date || null,
                  isActive: customer.is_active ?? true,
                  is_verified_via_link: customer.is_verified_via_link || false,
                  linked_at: customer.linked_at || null
                }));

                aggregatedCustomers = mergeCustomerLists(aggregatedCustomers, convertedCustomers);
              } else if (customersResponse.status !== 404) {
                console.warn('Tailor customers endpoint responded with status:', customersResponse.status);
              }
            } catch (customerError) {
              console.error('Failed to load customers from tailor endpoint:', customerError);
            }
          }

          // Merge with any locally created customers so the dashboard updates immediately.
          setCustomers((previous) => {
            const merged = mergeCustomerLists(previous, aggregatedCustomers);
            aggregatedCustomers = merged;
            return merged;
          });

          setCurrentCounts((prev) => ({
            ...prev,
            customers: aggregatedCustomers.length,
          }));
        } else {
          console.error('Failed to load customers from orders:', ordersResp.status);
        }
      } catch (_e) {
        console.error('Failed to load customers:', e);
      } finally {
        setIsLoadingCustomers(false);
      }

      try {
        setIsLoadingInventory(true);
        await refreshInventoryData();
      } catch (_error) {
        console.error('Failed to load inventory:', error);
      } finally {
        setIsLoadingInventory(false);
      }

      try {
        // Load suppliers from API - correct endpoint is /api/tailors/suppliers
        // Note: This endpoint requires Premium/Enterprise tier for inventory access
        const suppliersResponse = await fetch(`${API_BASE}/api/tailors/suppliers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (suppliersResponse.ok) {
          const suppliersData = await suppliersResponse.json();
          const suppliersList: Supplier[] = (suppliersData.suppliers || []).map((supplier: any) => ({
            id: supplier.id,
            company_name: supplier.company_name,
            contact_person: supplier.contact_person || undefined,
            email: supplier.email || undefined,
            phone: supplier.phone || undefined,
            address: supplier.address || undefined,
            city: supplier.city || undefined,
            country: supplier.country || 'India',
            payment_terms: supplier.payment_terms || undefined,
            is_active: supplier.is_active ?? true,
            total_items: supplier.total_items || 0
          }));
          setSuppliers(suppliersList);
        } else if (suppliersResponse.status === 403) {
          // User doesn't have inventory access (requires Premium tier)
          console.log('Suppliers endpoint requires Premium tier - skipping supplier load');
          setSuppliers([]);
        } else if (suppliersResponse.status === 404) {
          // Endpoint doesn't exist or no suppliers
          setSuppliers([]);
        } else {
          // Other errors (422, 500, etc.) - log but don't break the dashboard
          const errorText = await suppliersResponse.text().catch(() => 'Unknown error');
          console.warn(`Failed to load suppliers: ${suppliersResponse.status} - ${errorText}`);
          setSuppliers([]);
        }
      } catch (_e) {
        // Network errors or other exceptions - fail silently
        console.warn('Failed to load suppliers:', e);
        setSuppliers([]);
      }

      try {
        // Load schedule - calculate from orders due today
        setIsLoadingSchedule(true);
        // Schedule will be populated from orders array after orders are loaded
        setSchedule([]);
      } catch (_e) {
        console.error('Failed to load schedule:', e);
      } finally {
        setIsLoadingSchedule(false);
      }

      try {
        // Load orders
        setIsLoadingOrders(true);
        const resp = await fetch(`${API_BASE}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (resp.ok) {
          const data = await resp.json();
          const mapped = (data.orders || []).map((o: any) => ({
            id: String(o.id),
            order_number: o.order_number || o.orderNumber,
            customerName: o.customer_name || o.customer?.name || 'Customer',
            customer_phone: o.customer_phone || o.customer?.phone || o.customer?.phone_number || null,
            customerId: o.customer_id || o.customerId || o.customer?.id,
            garmentType: o.garmentType || o.order_type || 'Garment',
            status: o.status,
            dueDate: o.dueDate || o.due_date,
            // Prefer invoice total if available, otherwise use order amount
            amount: o.invoice?.total ?? o.amount ?? 0,
            priority: o.priority || 'medium',
            created_at: o.created_at || o.created_date || null,
          }));
          setOrders(mapped);
        } else {
          console.log(`Failed to fetch orders, status: ${resp.status}`);
          setOrders([]);
        }
      } catch (_e) {
        setOrders([]);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    loadDashboardData();
  }, [token, user]);

  // Update schedule from orders due today
  useEffect(() => {
    if (orders.length === 0) {
      setSchedule([]);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const ordersDueToday = orders
      .filter((order) => {
        if (!order.dueDate) return false;
        if (['completed', 'delivered', 'cancelled'].includes(order.status)) return false;
        
        const dueDate = new Date(order.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        
        return dueDate.getTime() === today.getTime();
      })
      .map((order) => ({
        id: `order-${order.id}`,
        type: 'delivery' as const,
        title: `Order ${(order as any).order_number || order.id}`,
        description: `${order.customerName} - ${order.garmentType}`,
        time: order.dueDate ? new Date(order.dueDate).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }) : 'Today'
      }));

    setSchedule(ordersDueToday);
  }, [orders]);

  // Update stats when orders or customers change
  useEffect(() => {
    const totalOrders = orders.length;
    // Pending orders: orders that are not completed, delivered, cancelled, or paid
    // Status can be 'new', 'pending', 'appointment_scheduled', 'in_progress', etc.
    const pendingOrders = orders.filter((o) => {
      const status = (o.status || '').toLowerCase();
      const completedStatuses = ['completed', 'delivered', 'cancelled', 'paid'];
      // If status is empty or not in completed list, it's pending
      return status && !completedStatuses.includes(status);
    }).length;
    // Revenue: only count orders that are paid (actual revenue, not pending)
    const totalRevenue = orders
      .filter((o) => o.status?.toLowerCase() === 'paid')
      .reduce((sum, order) => sum + (order.amount || 0), 0);

    // Active customers: count all customers who are active (isActive !== false)
    // This includes customers who may not have orders yet but are in the system
    const activeCustomersCount = customers.filter((c) => c.isActive !== false).length;

    setStats([
      { title: 'Total Orders', value: String(totalOrders), change: '+0%', trend: 'up', icon: Package },
      { title: 'Active Customers', value: String(activeCustomersCount || 0), change: '+0%', trend: 'up', icon: Users },
      { title: 'Monthly Revenue', value: formatCurrency(totalRevenue), change: '+0%', trend: 'up', icon: CreditCard },
      { title: 'Pending Orders', value: String(pendingOrders), change: '+0%', trend: 'down', icon: Clock },
    ]);
  }, [orders, customers]);

  // Enhanced Inventory Management Functions
  const closeQuickAddModal = () => {
    setIsQuickAddOpen(false);
    setShowAdvancedFields(false);
    setModalError('');
    setQuickAddData(QUICK_ADD_DEFAULT);
    setAdvancedData(ADVANCED_DEFAULT);
    setModalMode('create');
    setEditingItemId(null);
    setShowUpgradePrompt(false);
  };

  const openCreateModal = async () => {
    // Check access before opening modal
    const hasAccess = await checkInventoryAccess();
    if (!hasAccess) {
      setModalMode('create');
      setEditingItemId(null);
      setQuickAddData(QUICK_ADD_DEFAULT);
      setAdvancedData(ADVANCED_DEFAULT);
      setShowAdvancedFields(false);
      setModalError('Inventory management is available on the Premium plan. Please upgrade to continue.');
      setShowUpgradePrompt(true);
      setIsQuickAddOpen(true);
      return;
    }

    setModalMode('create');
    setEditingItemId(null);
    setQuickAddData(QUICK_ADD_DEFAULT);
    setAdvancedData(ADVANCED_DEFAULT);
    setShowAdvancedFields(false);
    setModalError('');
    setShowUpgradePrompt(false);
    setIsQuickAddOpen(true);
  };

  const openEditModal = (item: RealInventoryItem) => {
    setModalMode('edit');
    setEditingItemId(item.id);
    setQuickAddData({
      name: item.name || '',
      quantity: item.current_stock !== undefined ? String(item.current_stock) : '',
      unit: item.unit || 'pieces',
      category: item.category || ''
    });
    setAdvancedData({
      description: item.description || '',
      sku: item.sku || '',
      subcategory: item.subcategory || '',
      supplier_id: item.supplier && item.supplier.id ? String(item.supplier.id) : '',
      minimum_stock: item.minimum_stock !== undefined && item.minimum_stock !== null ? String(item.minimum_stock) : String(DEFAULT_MIN_STOCK),
      maximum_stock: item.maximum_stock !== undefined && item.maximum_stock !== null ? String(item.maximum_stock) : '',
      cost_per_unit: item.cost_per_unit !== undefined && item.cost_per_unit !== null ? String(item.cost_per_unit) : '',
      selling_price: item.selling_price !== undefined && item.selling_price !== null ? String(item.selling_price) : '',
      color: item.color || '',
      notes: item.notes || ''
    });
    setShowAdvancedFields(true);
    setModalError('');
    setShowUpgradePrompt(false);
    setIsQuickAddOpen(true);
  };

  const buildPayload = () => {
    const quantityValue = parseFloat(quickAddData.quantity);
    const minimumStockValue = parseFloat(advancedData.minimum_stock);

    // Generate SKU if not provided
    const generateSKU = () => {
      const timestamp = Date.now();
      const categoryPrefix = quickAddData.category?.toLowerCase() || 'item';
      const namePrefix = quickAddData.name.trim().toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 8);
      return `${categoryPrefix}-${namePrefix}-${timestamp}`;
    };

    return {
      name: quickAddData.name.trim(),
      category: quickAddData.category ? quickAddData.category.trim() : null,
      current_stock: Number.isNaN(quantityValue) ? 0 : quantityValue,
      minimum_stock: Number.isNaN(minimumStockValue) ? DEFAULT_MIN_STOCK : minimumStockValue,
      unit: quickAddData.unit,
      description: advancedData.description || null,
      sku: advancedData.sku?.trim() || generateSKU(), // Auto-generate if empty
      subcategory: advancedData.subcategory || null,
      supplier_id: advancedData.supplier_id ? parseInt(advancedData.supplier_id, 10) : null,
      maximum_stock: advancedData.maximum_stock ? parseFloat(advancedData.maximum_stock) : null,
      cost_per_unit: advancedData.cost_per_unit ? parseFloat(advancedData.cost_per_unit) : null,
      selling_price: advancedData.selling_price ? parseFloat(advancedData.selling_price) : null,
      color: advancedData.color || null,
      notes: advancedData.notes || null
    };
  };

  const debugUserTier = async () => {
    try {
      const response = await fetchWithFallback('/api/tailors/inventory/debug-user', {
        headers: buildAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 DEBUG: User tier info:', data);
        alert(`User Tier Debug:\nEmail: ${data.user_email}\nTier: ${data.tier_attribute}\nResolved: ${data.resolved_tier}\nHas Access: ${data.has_access}\nFeatures: ${JSON.stringify(data.features_enabled)}`);
      } else {
        const error = await response.json();
        console.log('🔍 DEBUG: Debug endpoint error:', error);
        alert(`Debug endpoint error: ${error.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('🔍 DEBUG: Debug endpoint failed:', error);
      alert(`Debug endpoint failed: ${error}`);
    }
  };

  const testInventoryAccess = async () => {
    try {
      console.log('🔍 Testing inventory access...');
      const response = await fetchWithFallback('/api/tailors/inventory/test-access', {
        method: 'GET',
        headers: buildAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Inventory access test passed:', data);
        alert(`Inventory access test passed!\nUser: ${data.user_email}\nTier: ${data.user_tier}\nProfile ID: ${data.profile_id}`);
      } else {
        const errorData = await response.json();
        console.log('❌ Inventory access test failed:', errorData);
        alert(`Inventory access test failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (_error) {
      console.error('❌ Inventory access test error:', error);
      alert(`Inventory access test error: ${error}`);
    }
  };

  const checkInventoryAccess = async () => {
    try {
      const response = await fetchWithFallback('/api/tailors/inventory/debug-user', {
        headers: buildAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.has_access;
      }
      return false;
    } catch (_error) {
      console.error('🔍 DEBUG: Access check failed:', error);
      return false;
    }
  };

  const handleQuickAddSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    console.log('🔍 DEBUG: handleQuickAddSubmit called');
    console.log('🔍 DEBUG: quickAddData:', quickAddData);
    console.log('🔍 DEBUG: advancedData:', advancedData);
    console.log('🔍 DEBUG: modalMode:', modalMode);
    console.log('🔍 DEBUG: editingItemId:', editingItemId);
    console.log('🔍 DEBUG: FALLBACK_URLS:', FALLBACK_URLS);

    if (!quickAddData.name.trim()) {
      setModalError('Please add an item name.');
      return;
    }

    if (!quickAddData.category) {
      setModalError('Please select a category.');
      return;
    }

    setModalError('');
    setIsInventorySubmitting(true);

    try {
      const payload = buildPayload();
      console.log('🔍 DEBUG: Built payload:', payload);
      
      const endpointPath = editingItemId ? `/inventory/${editingItemId}` : '/inventory';
      const method = editingItemId ? 'PUT' : 'POST';
      
      console.log('🔍 DEBUG: Making request to:', endpointPath, 'with method:', method);
      
      const response = await inventoryFetch(endpointPath, {
        method,
        headers: buildAuthHeaders(),
        body: JSON.stringify(payload)
      });

      console.log('🔍 DEBUG: Response status:', response.status);
      console.log('🔍 DEBUG: Response ok:', response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log('🔍 DEBUG: Success response data:', responseData);
        await refreshInventoryData();
        closeQuickAddModal();
        setEditingItemId(null);
        console.log('🔍 DEBUG: Successfully saved item');
      } else {
        const errorData = await response.json();
        console.log('🔍 DEBUG: Error response:', errorData);
        console.log('🔍 DEBUG: Error status:', response.status);
        console.log('🔍 DEBUG: Error detail:', errorData.detail);
        
        // Check if it's a plan restriction error
        if (response.status === 403 && errorData.detail && errorData.detail.includes('Premium plan')) {
          setModalError('Inventory management is available on the Premium plan. Please upgrade to continue.');
          setShowUpgradePrompt(true);
          // Don't close the modal, let user see the upgrade message
          return;
        }
        
        setModalError(errorData.detail || 'Failed to save item');
      }
    } catch (_error) {
      console.error('❌ ERROR: Error saving inventory item:', error);
      setModalError('Network error occurred');
    } finally {
      setIsInventorySubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    
    // Show archive modal by default (keeping existing behavior)
    setItemToArchive(item);
    setIsArchiveModalOpen(true);
  };

  const handleArchiveItem = async (itemId: number) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    
    setItemToArchive(item);
    setIsArchiveModalOpen(true);
  };

  const handleDeleteItemPermanent = async (itemId: number) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const confirmArchiveItem = async () => {
    if (!itemToArchive) return;

    setIsInventorySubmitting(true);
    setInventoryError('');
    setIsArchiveModalOpen(false);

    try {
      const response = await inventoryFetch(`/inventory/${itemToArchive.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders()
      });

      if (response.ok) {
        // Success - refresh inventory data
        await refreshInventoryData();
        setInventorySuccessMessage('Item archived successfully!');
        setTimeout(() => setInventorySuccessMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to archive inventory item:', response.status, errorData);
        setInventoryError(`Failed to archive item: ${errorData.detail || 'Please try again'}`);
      }
    } catch (_error) {
      console.error('Error archiving inventory item:', error);
      setInventoryError('Failed to archive item. Please check your connection and try again.');
    } finally {
      setIsInventorySubmitting(false);
      setItemToArchive(null);
    }
  };

  const handleUnarchiveItem = async (itemId: number) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    setIsInventorySubmitting(true);
    setInventoryError('');

    try {
      const response = await inventoryFetch(`/inventory/${itemId}`, {
        method: 'PATCH',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: true })
      });

      if (response.ok) {
        // Success - refresh inventory data
        await refreshInventoryData();
        setInventorySuccessMessage('Item unarchived successfully!');
        setTimeout(() => setInventorySuccessMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to unarchive inventory item:', response.status, errorData);
        setInventoryError(`Failed to unarchive item: ${errorData.detail || 'Please try again'}`);
      }
    } catch (_error) {
      console.error('Error unarchiving inventory item:', error);
      setInventoryError('Failed to unarchive item. Please check your connection and try again.');
    } finally {
      setIsInventorySubmitting(false);
    }
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    setIsInventorySubmitting(true);
    setInventoryError('');
    setIsDeleteModalOpen(false);

    try {
      console.log('🗑️ Deleting item:', itemToDelete.id, itemToDelete.name);
      
      // For now, we'll use the same DELETE endpoint but with a different message
      // In the future, you might want a separate hard delete endpoint
      const response = await inventoryFetch(`/inventory/${itemToDelete.id}?permanent=true`, {
        method: 'DELETE',
        headers: buildAuthHeaders()
      });

      console.log('🗑️ Delete response:', response.status, response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log('🗑️ Delete response data:', responseData);
        
        // Success - refresh inventory data with a small delay to ensure backend is updated
        setTimeout(async () => {
          await refreshInventoryData();
          console.log('🗑️ Inventory refreshed after delete');
        }, 500);
        
        setInventorySuccessMessage('Item permanently deleted!');
        setTimeout(() => setInventorySuccessMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to delete inventory item:', response.status, errorData);
        setInventoryError(`Failed to delete item: ${errorData.detail || 'Please try again'}`);
      }
    } catch (_error) {
      console.error('Error deleting inventory item:', error);
      setInventoryError('Failed to delete item. Please check your connection and try again.');
    } finally {
      setIsInventorySubmitting(false);
      setItemToDelete(null);
    }
  };

  const sendCustomerInvitation = async (customer: any, email: string, tempPassword: string) => {
    try {
      // For now, we'll just log the invitation details
      // In a real implementation, this would send email/SMS
      console.log('Sending customer invitation:', {
        customerName: customer.name || customerFormData.name,
        email: email,
        tempPassword: tempPassword,
        phone: customerFormData.phone ? `${selectedCountryCode}${customerFormData.phone}` : null,
        appDownloadLink: 'https://masterji.com/download', // Replace with actual app store links
        instructions: 'Please download the Masterji app and log in with the temporary password. You will be required to change your password on first login.'
      });
      
      // TODO: Implement actual email/SMS sending
      // This could call a backend endpoint like /api/send-invitation
      // or integrate with email service like SendGrid, AWS SES, etc.
      
    } catch (_error) {
      console.error('Failed to send invitation:', error);
      // Don't fail the entire customer creation if invitation fails
    }
  };

  const handleAdjustStock = async (item: RealInventoryItem, delta: number) => {
    const nextValue = Math.max(0, item.current_stock + delta);
    if (nextValue === item.current_stock) {
      return;
    }

    const previousInventory = inventory;
    setInventory(prev =>
      prev.map(current => (current.id === item.id ? { ...current, current_stock: nextValue } : current))
    );

    try {
      const response = await inventoryFetch(`/inventory/${item.id}`, {
        method: 'PATCH',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ current_stock: nextValue })
      });

      if (!response.ok) {
        throw new Error('Adjustment failed');
      }

      await refreshInventoryData();
    } catch (_err) {
      console.error(err);
      setInventory(previousInventory);
      setInventoryError('Could not update stock. Please try again.');
    }
  };

  const handleSetStock = async (item: RealInventoryItem) => {
    const value = window.prompt('Adjust stock for ' + item.name, String(item.current_stock));
    if (value === null) {
      return;
    }

    const nextValue = parseFloat(value);
    if (Number.isNaN(nextValue) || nextValue < 0) {
      setInventoryError('Please enter a valid stock value.');
      return;
    }

    if (nextValue === item.current_stock) {
      return;
    }

    const previousInventory = inventory;
    setInventory(prev =>
      prev.map(current => (current.id === item.id ? { ...current, current_stock: nextValue } : current))
    );

    try {
      const response = await inventoryFetch(`/inventory/${item.id}`, {
        method: 'PATCH',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ current_stock: nextValue })
      });

      if (!response.ok) {
        throw new Error('Adjustment failed');
      }

      await refreshInventoryData();
    } catch (_err) {
      console.error(err);
      setInventory(previousInventory);
      setInventoryError('Could not update stock. Please try again.');
    }
  };

  // Inventory filtering and sorting functions
  const filteredAndSortedInventory = React.useMemo(() => {
    const filtered = inventory.filter(item => {
      // Show active items by default, or archived items if toggle is on
      const isActive = showArchivedItems ? (item.is_active === false) : (item.is_active !== false);
      
      // Search filter
      const matchesSearch = !inventorySearchTerm || 
        item.name.toLowerCase().includes(inventorySearchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(inventorySearchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(inventorySearchTerm.toLowerCase()) ||
        item.subcategory?.toLowerCase().includes(inventorySearchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(inventorySearchTerm.toLowerCase());
      
      // Category filter
      const matchesCategory = !inventoryFilterCategory || 
        item.category.toLowerCase() === inventoryFilterCategory.toLowerCase();
      
      return isActive && matchesSearch && matchesCategory;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (inventorySortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'stock':
          aValue = a.current_stock;
          bValue = b.current_stock;
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (inventorySortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [inventory, inventorySearchTerm, inventoryFilterCategory, inventorySortBy, inventorySortOrder, showArchivedItems]);

  // Get unique categories for filter dropdown
  const inventoryCategories = React.useMemo(() => {
    const categories = [...new Set(inventory.map(item => item.category))];
    return categories.sort();
  }, [inventory]);

  const recentActivity = React.useMemo(() => {
    return [...inventory]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);
  }, [inventory]);

  // Auto-switch to archived view only if user hasn't manually interacted with the toggle
  useEffect(() => {
    if (!inventory.length) {
      return;
    }
    const hasActiveItems = inventory.some(item => item.is_active !== false);
    // Only auto-switch if there are no active items AND user hasn't manually toggled
    // We'll track this with a ref to avoid infinite loops
    if (!hasActiveItems && !showArchivedItems) {
      // Only auto-switch on initial load, not after user interaction
      const hasUserInteracted = localStorage.getItem('inventory-archive-toggle-interacted');
      if (!hasUserInteracted) {
        setShowArchivedItems(true);
      }
    }
  }, [inventory]); // Remove showArchivedItems from dependencies to prevent loops

  const totalInventoryTarget = showArchivedItems ? inventory.length : inventoryStats.total_items;
  const archivedHidden = archivedInventoryCount > 0 && !showArchivedItems;

  // Temporarily remove OnboardingDashboard wrapper to debug
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header */}
      <EnhancedHeader 
        title={`${user?.name || 'Business'}'s Master Ji`}
        showBackButton={true}
        backTo="/"
        onGuidedSetupClick={() => setShowGuidedSetup(true)}
        userTier={userTier}
        onUpgradeClick={() => setShowTierUpgrade(true)}
      />

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'orders', label: 'Orders', icon: Package },
              { id: 'customers', label: 'Customers', icon: Users },
              { id: 'messaging', label: 'Messaging', icon: MessageSquare, badge: totalUnread },
              { id: 'payments', label: 'Payments', icon: CreditCard },
              { id: 'receipts', label: 'Receipts', icon: Receipt },
              { id: 'inventory', label: 'Inventory', icon: FileText },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'profile', label: 'Profile', icon: Settings },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className="ml-1 text-gray-500">
                      ({tab.badge > 99 ? '99+' : tab.badge})
                    </span>
                  )}
                </span>
              </button>
            ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {dashboardView === 'enhanced' ? (
              <EnhancedDashboardView />
            ) : (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {stats.map((stat, index) => (
                    <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center">
                          <stat.icon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex items-center mt-4">
                        {stat.trend === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-sm font-medium ${
                          stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {stat.change}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">from last month</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tier Restriction Banners */}
                <TierRestrictionBanner
                  tierId={userTier}
                  currentCounts={currentCounts}
                  onUpgrade={() => setShowTierUpgrade(true)}
                  type="customer"
                />
                <TierRestrictionBanner
                  tierId={userTier}
                  currentCounts={currentCounts}
                  onUpgrade={() => setShowTierUpgrade(true)}
                  type="karigar"
                />

                {/* Recent Orders */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Recent Orders</h2>
                  </div>
                  
                  <DataTable
                    data={sortedOrders}
                    columns={orderColumns}
                    persona="business"
                    searchable={false}
                    filterable={true}
                    pagination={false}
                    sortable={true}
                    actions={orderActions}
                    onAction={handleOrderAction}
                    emptyMessage="No orders found"
                    loading={isLoadingOrders}
                    onRowClick={(row: any) => {
                      const orderNumber = row.order_number || row.id;
                      navigate(`/order/${orderNumber}/details`);
                    }}
                  />
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                      <button 
                        className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-600/90 transition-colors"
                        onClick={() => navigate('/business/orders/new')}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Order
                      </button>
                      <button 
                        onClick={handleAddCustomer}
                        className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Add Customer
                      </button>
                      <button 
                        className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        onClick={() => setActiveTab('inventory')}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Update Inventory
                      </button>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Schedule</h3>
                    <div className="space-y-3">
                      {isLoadingSchedule ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading schedule...</p>
                        </div>
                      ) : schedule.length > 0 ? (
                        schedule.map((item) => (
                          <div key={item.id} className={`flex items-center justify-between p-3 rounded-lg ${
                            item.type === 'appointment' ? 'bg-blue-600/5' : 'bg-green-50'
                          }`}>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.title}</p>
                              <p className="text-xs text-gray-600">{item.description}</p>
                            </div>
                            <span className="text-xs text-gray-500">{item.time}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-500">No appointments or deliveries scheduled for today</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Low Stock Alerts</h3>
                    <div className="space-y-3">
                      {isLoadingInventory ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading inventory...</p>
                        </div>
                      ) : inventory.filter(item => item.low_stock).length > 0 ? (
                        inventory.filter(item => item.low_stock).map(item => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-gray-600">{item.current_stock} {item.unit} left</p>
                            </div>
                            <button 
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                              onClick={() => setActiveTab('inventory')}
                            >
                              Reorder
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-500">No low stock alerts</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-6" data-feature="order-management" data-tour="tailor-orders">
            <SectionHeader
              title="Order Management"
              description="Monitor every order stage—from intake to delivery—and quickly act on items that need attention."
              actions={
                <button 
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-600/90 transition-colors flex items-center justify-center" 
                  onClick={() => navigate('/business/orders/new')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Order
                </button>
              }
            />
            
            {/* Order Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {orderStats.map((stat) => (
                <StatCard key={stat.id} stat={stat} />
              ))}
            </div>
            
            <div className={`${cardClasses} p-6`}>
              <DataTable
                data={sortedOrders}
                columns={orderColumns}
                persona="business"
                searchable={false}
                filterable={true}
                pagination={true}
                sortable={true}
                actions={orderActions}
                onAction={handleOrderAction}
                emptyMessage="No orders found."
                getRowClassName={getOrderRowClassName}
              />
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Customer Directory</h2>
                  <p className="text-gray-600 mt-1">Keep customer details organised, track engagement, and send invitations in one place.</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={handleAddCustomer}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Customer
                  </button>
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search customers by name, email, or phone..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                />
              </div>
            </div>

            {/* Customer Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {customerStats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${statToneStyles[stat.tone ?? 'info']}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{stat.description}</p>
                  </div>
                );
              })}
            </div>
            
            {/* Customer Directory */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Customer Directory</h3>
                <div className="text-sm text-gray-500">
                  {filteredCustomers.length} of {customers.length} customers
                </div>
              </div>
              
              {isLoadingCustomers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading customers...</p>
                </div>
              ) : filteredCustomers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCustomers.map((customer) => (
                  <div 
                    key={customer.id} 
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleCustomerClick(customer)}
                  >
                    {/* Customer Avatar and Status */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="relative flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-sm">
                              {customer.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          {/* Verification Badge */}
                          {customer.is_verified_via_link && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm" title="Customer registered via link">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900 truncate">{customer.name}</h3>
                            {customer.is_verified_via_link && (
                              <span className="flex-shrink-0 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1" title="Verified - Customer registered via link">
                                <CheckCircle className="w-3 h-3" />
                                <span className="hidden sm:inline">Verified</span>
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">{customer.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        {customer.orderCount > 0 ? (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                            Active
                          </span>
                        ) : (
                          <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Customer Details */}
                    <div className="space-y-2 mb-4">
                      {customer.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="w-4 h-4 mr-2">📞</span>
                          {customer.phone}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="w-4 h-4 mr-2">📦</span>
                        {customer.orderCount} {customer.orderCount === 1 ? 'order' : 'orders'}
                      </div>
                      {customer.lastOrderDate && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="w-4 h-4 mr-2">📅</span>
                          Last order: {new Date(customer.lastOrderDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      {customer.orderCount === 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvitation(customer);
                          }}
                          className="flex-1 bg-orange-100 text-orange-700 text-xs px-3 py-2 rounded hover:bg-orange-200 transition-colors"
                        >
                          Resend Invitation
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendReminder(customer);
                          }}
                          className="flex-1 bg-blue-100 text-blue-700 text-xs px-3 py-2 rounded hover:bg-blue-200 transition-colors"
                        >
                          Send Reminder
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCustomerClick(customer);
                        }}
                        className="flex-1 bg-gray-100 text-gray-700 text-xs px-3 py-2 rounded hover:bg-gray-200 transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomer(customer.id);
                        }}
                        className="bg-red-100 text-red-700 text-xs px-3 py-2 rounded hover:bg-red-200 transition-colors"
                        title="Remove Customer from List"
                      >
                        <Trash className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                <p className="text-gray-600 mb-6">
                  {customerSearchTerm ? 'Try adjusting your search terms' : 'Start building your customer base'}
                </p>
                <button 
                  onClick={handleAddCustomer}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-600/90 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2 inline" />
                  Add Your First Customer
                </button>
              </div>
            )}
            </div>
          </div>
        )}

        {activeTab === 'messaging' && (
          <TailorMessagingPage />
        )}

        {activeTab === 'payments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Payment Management</h2>
                <p className="text-gray-600 mt-1">Track payments, pending amounts, and revenue</p>
              </div>
            </div>

            {/* Payment Methods Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
                  <p className="text-sm text-gray-600 mt-1">Manage your saved payment methods for faster checkout</p>
                </div>
                <button
                  onClick={() => setShowPaymentMethods(true)}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Manage Payment Methods</span>
                </button>
              </div>
            </div>

            {/* Payment Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Revenue (Paid) */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        orders
                          .filter((o) => o.status?.toLowerCase() === 'paid')
                          .reduce((sum, order) => sum + (order.amount || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">From paid orders</p>
              </div>

              {/* Pending Payments */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        orders
                          .filter((o) => {
                            const status = (o.status || '').toLowerCase();
                            return status && !['paid', 'completed', 'delivered', 'cancelled'].includes(status);
                          })
                          .reduce((sum, order) => sum + (order.amount || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {orders.filter((o) => {
                    const status = (o.status || '').toLowerCase();
                    return status && !['paid', 'completed', 'delivered', 'cancelled'].includes(status);
                  }).length} unpaid orders
                </p>
              </div>

              {/* This Month Revenue */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">This Month</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(
                        orders
                          .filter((o) => {
                            if (o.status?.toLowerCase() !== 'paid') return false;
                            const orderDate = o.created_at ? new Date(o.created_at) : null;
                            if (!orderDate) return false;
                            const now = new Date();
                            return orderDate.getMonth() === now.getMonth() && 
                                   orderDate.getFullYear() === now.getFullYear();
                          })
                          .reduce((sum, order) => sum + (order.amount || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Paid this month</p>
              </div>
            </div>

            {/* Pending Payments Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Pending Payments</h3>
                <p className="text-sm text-gray-600 mt-1">Orders awaiting payment</p>
              </div>
              
              {isLoadingOrders ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading payments...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders
                        .filter((o) => {
                          const status = (o.status || '').toLowerCase();
                          return status && !['paid', 'completed', 'delivered', 'cancelled'].includes(status);
                        })
                        .map((order) => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-blue-600">
                                #{(order as any).order_number || order.id}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{order.customerName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(order.amount || 0)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                order.status?.toLowerCase() === 'in_progress' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {order.status || 'Pending'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => navigate(`/order/${(order as any).order_number || order.id}/timeline`)}
                                className="text-blue-600 hover:text-blue-900 mr-4"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  
                  {orders.filter((o) => {
                    const status = (o.status || '').toLowerCase();
                    return status && !['paid', 'completed', 'delivered', 'cancelled'].includes(status);
                  }).length === 0 && (
                    <div className="text-center py-12">
                      <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No pending payments</p>
                      <p className="text-sm text-gray-500 mt-1">All orders have been paid</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'receipts' && (
          <ReceiptsTab userRole={user?.role === 'admin' ? 'admin' : 'tailor'} />
        )}

        {activeTab === 'inventory' && (
          <div className="space-y-6">
            {/* Check if user has inventory access (Premium+ tier) */}
            {userTier === 'free' || userTier === 'basic' ? (
              /* Upgrade Required for Inventory */
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Package className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Inventory Management</h2>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Track your materials, monitor stock levels, and manage suppliers with our advanced inventory system.
                  </p>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Available in Premium Plan</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        Track Stock Levels
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        Supplier Management
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        Low Stock Alerts
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => {
                      setUpgradeReason('general');
                      setShowTierUpgrade(true);
                    }}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto font-medium"
                  >
                    <TrendingUp className="w-5 h-5" />
                    Upgrade to Premium
                  </button>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    Starting at ?2,499/month  Cancel anytime
                  </p>
                </div>
              </div>
            ) : (
              /* Enhanced Inventory Interface for Premium+ Users */
              <>
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Inventory Management</h2>
                      <p className="text-gray-600 mt-1">Keep an eye on materials, get alerts before you run out, and record new stock in seconds.</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={openCreateModal}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                        disabled={isInventorySubmitting}
                      >
                        <Plus className="w-4 h-4" />
                        Quick add item
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search and Filter Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search inventory by name, category, SKU..."
                          value={inventorySearchTerm}
                          onChange={(e) => setInventorySearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                        />
                      </div>
                    </div>
                    
                    {/* Category Filter */}
                    <div className="lg:w-48">
                      <select
                        value={inventoryFilterCategory}
                        onChange={(e) => setInventoryFilterCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                      >
                        <option value="">All Categories</option>
                        {inventoryCategories.map(category => (
                          <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Sort Options */}
                    <div className="flex gap-2">
                      <select
                        value={inventorySortBy}
                        onChange={(e) => setInventorySortBy(e.target.value as any)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="name">Sort by Name</option>
                        <option value="stock">Sort by Stock</option>
                        <option value="category">Sort by Category</option>
                        <option value="created_at">Sort by Date Added</option>
                      </select>
                      
                      <button
                        onClick={() => setInventorySortOrder(inventorySortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                        title={`Sort ${inventorySortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                      >
                        {inventorySortOrder === 'asc' ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                      </button>
                      
                      {/* Show Archived Items Toggle */}
                      <button
                        onClick={() => {
                          // Track that user has manually interacted with the toggle
                          localStorage.setItem('inventory-archive-toggle-interacted', 'true');
                          setShowArchivedItems(!showArchivedItems);
                        }}
                        className={`px-3 py-2 border rounded-lg transition-colors flex items-center gap-1 ${
                          showArchivedItems 
                            ? 'bg-blue-600 text-white border-blue-600' 
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                        title={showArchivedItems ? 'Hide archived items' : 'Show archived items'}
                      >
                        <Archive className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {showArchivedItems ? 'Hide Archived' : 'Show Archived'}
                        </span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Results Summary */}
                  <div className="mt-3 text-sm text-gray-600">
                    Showing {filteredAndSortedInventory.length} of {totalInventoryTarget} items
                    {showArchivedItems && archivedInventoryCount > 0 && (
                      <span className="ml-2 text-orange-600 font-medium">
                        (showing archived items only)
                      </span>
                    )}
                    {!showArchivedItems && archivedInventoryCount > 0 && (
                      <span className="ml-2 text-orange-600 font-medium">
                        ({archivedInventoryCount} archived items hidden)
                      </span>
                    )}
                    {inventorySearchTerm && (
                      <span className="ml-2">
                        for "{inventorySearchTerm}"
                      </span>
                    )}
                    {inventoryFilterCategory && (
                      <span className="ml-2">
                        in {inventoryFilterCategory}
                      </span>
                    )}
                  </div>
                </div>

                {inventoryError ? (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">{inventoryError}</p>
                      <button
                        onClick={async () => {
                          setInventoryError('');
                          await refreshInventoryData();
                        }}
                        className="mt-2 inline-flex items-center gap-2 text-sm text-yellow-900 hover:underline"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Try again
                      </button>
                  </div>
                  </div>
                ) : null}

                {inventorySuccessMessage ? (
                  <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <p>{inventorySuccessMessage}</p>
                  </div>
                ) : null}

                <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    {isLoadingInventory ? (
                      <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                        <p className="text-gray-600 mt-4">Pulling the latest items</p>
                      </div>
                    ) : !inventory.length ? (
                      <div className="py-16 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Nothing in stock yet</h3>
                        <p className="text-gray-600 mb-6">
                          Add your first item to start tracking fabric, trims, and materials in one place.
                        </p>
                        <button
                          onClick={openCreateModal}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                          disabled={isInventorySubmitting}
                        >
                          <Plus className="w-4 h-4" />
                          Add your first item
                        </button>
                      </div>
                    ) : filteredAndSortedInventory.length === 0 ? (
                      <div className="py-16 text-center">
                        <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No items found</h3>
                        <p className="text-gray-600 mb-6">
                          Try adjusting your search terms or filters to find what you're looking for.
                        </p>
                        {archivedInventoryCount > 0 && !showArchivedItems ? (
                          <p className="text-sm text-orange-600 mb-6">
                            All {archivedInventoryCount} item{archivedInventoryCount === 1 ? '' : 's'} are archived.
                            Select "Show Archived" to review them.
                          </p>
                        ) : null}
                        <button
                          onClick={() => {
                            setInventorySearchTerm('');
                            setInventoryFilterCategory('');
                            // Don't reset archive toggle - that's a separate user preference
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                        >
                          <X className="w-4 h-4" />
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      <div className="max-h-[600px] overflow-y-auto pr-2 space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        {filteredAndSortedInventory.map(item => (
                          <motion.div
                            key={item.id}
                            layout
                            className="border border-gray-200 rounded-xl p-4 hover:border-blue-200 transition-colors"
                          >
                            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                                  {item.low_stock ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                      Low stock
                                    </span>
                                  ) : null}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {(item.category ? item.category[0].toUpperCase() + item.category.slice(1) : 'Uncategorized') + (item.subcategory ? '  ' + item.subcategory : '')}
                                </p>
                                {item.supplier && item.supplier.company_name ? (
                                  <p className="text-sm text-gray-500 mt-1">Supplier: {item.supplier.company_name}</p>
                                ) : null}
                                {item.notes ? (
                                  <p className="text-sm text-gray-500 mt-1">Notes: {item.notes}</p>
                                ) : null}
                      </div>

                              <div className="flex flex-col items-start gap-2">
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => handleAdjustStock(item, -1)}
                                    className="p-2 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
                                    aria-label="Decrease stock"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <div className="text-xl font-semibold text-gray-900">
                                    {item.current_stock} {item.unit}
                                  </div>
                                  <button
                                    onClick={() => handleAdjustStock(item, 1)}
                                    className="p-2 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
                                    aria-label="Increase stock"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {'Minimum: ' + item.minimum_stock + ' ' + item.unit + (item.maximum_stock ? '  Max: ' + item.maximum_stock + ' ' + item.unit : '')}
                                </p>
                    </div>
                  </div>
                  
                            <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                onClick={() => {
                                  setSelectedItem(item);
                                  setIsDetailOpen(true);
                                }}
                                className="px-3 py-1 text-sm font-medium rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100"
                              >
                                View details
                              </button>
                              <button
                                onClick={() => handleSetStock(item)}
                                className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                              >
                                Adjust quantity
                              </button>
                              {item.is_active !== false ? (
                                <button
                                  onClick={() => handleArchiveItem(item.id)}
                                  className="px-3 py-1 text-sm font-medium rounded-full bg-orange-50 text-orange-700 hover:bg-orange-100"
                                  disabled={isInventorySubmitting}
                                >
                                  Archive
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUnarchiveItem(item.id)}
                                  className="px-3 py-1 text-sm font-medium rounded-full bg-green-50 text-green-700 hover:bg-green-100"
                                  disabled={isInventorySubmitting}
                                >
                                  Unarchive
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteItemPermanent(item.id)}
                                className="px-3 py-1 text-sm font-medium rounded-full bg-red-50 text-red-700 hover:bg-red-100"
                                disabled={isInventorySubmitting}
                              >
                                Delete
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Key Metrics */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">At a glance</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Total items</p>
                            <p className="text-xl font-semibold text-gray-900">{inventoryStats.total_items}</p>
                            {archivedInventoryCount > 0 && (
                              <p className="text-xs text-orange-600 mt-1">
                                {archivedInventoryCount} archived item{archivedInventoryCount === 1 ? '' : 's'} hidden
                                from totals
                              </p>
                            )}
                          </div>
                          <Package className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Units on hand</p>
                            <p className="text-xl font-semibold text-gray-900">{inventoryStats.total_stock}</p>
                          </div>
                          <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Low stock items</p>
                            <p className="text-xl font-semibold text-gray-900">{inventoryStats.low_stock_items}</p>
                          </div>
                          <AlertTriangle className="w-6 h-6 text-orange-500" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Inventory value</p>
                            <p className="text-xl font-semibold text-gray-900">{formatCurrency(inventoryStats.total_value)}</p>
                          </div>
                          <ChevronRight className="w-6 h-6 text-gray-400" />
                        </div>
                      </div>
                    </div>
                
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                        <button
                          onClick={refreshInventoryData}
                          className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>
                      {recentActivity.length ? (
                        <ul className="space-y-3">
                          {recentActivity.map(item => (
                            <li key={item.id} className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                                <Clock3 className="w-4 h-4" />
                              </div>
                    <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {item.name + '  ' + item.current_stock + ' ' + item.unit}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {'Updated ' + new Date(item.updated_at).toLocaleString()}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">Add items to see a timeline of changes.</p>
                      )}
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">Stock rules</h2>
                          <p className="text-sm text-gray-600 mt-1">
                            Items inherit a minimum stock of {DEFAULT_MIN_STOCK} units unless you set a custom value.
                          </p>
                        </div>
                      <button 
                          onClick={() => setShowStockRules(prev => !prev)}
                          className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                      >
                          <Info className="w-4 h-4" />
                          {showStockRules ? 'Hide details' : 'Adjust rules'}
                      </button>
                      </div>
                      {showStockRules ? (
                        <div className="mt-4 space-y-3 text-sm text-gray-600">
                          <p>
                            Change the default minimum on an item when you open "More details" in the quick add form.
                          </p>
                          <p>
                            This baseline keeps your low-stock alerts consistent across fabrics, trims, and tools.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Quick Add Modal */}
            {isQuickAddOpen ? (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    closeQuickAddModal();
                  }
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="w-full max-w-4xl bg-white rounded-2xl shadow-xl max-h-[90vh] flex flex-col"
                >
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {modalMode === 'edit' ? 'Edit inventory item' : 'Quick add inventory'}
                      </h2>
                      <p className="text-sm text-gray-600">
                        Capture the essentials now and add more detail when you need it.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={debugUserTier} 
                        className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                      >
                        Debug Tier
                      </button>
                      <button 
                        onClick={testInventoryAccess} 
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                      >
                        Test Access
                      </button>
                      <button onClick={closeQuickAddModal} className="text-gray-500 hover:text-gray-700">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    <form
                      id="quickAddForm"
                      onSubmit={handleQuickAddSubmit}
                      className="px-6 py-6 space-y-6"
                    >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Item name *</label>
                        <input
                          type="text"
                          value={quickAddData.name}
                          onChange={event => setQuickAddData(prev => ({ ...prev, name: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder="e.g., Cotton sateen"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={quickAddData.quantity}
                          onChange={event => setQuickAddData(prev => ({ ...prev, quantity: event.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <select
                          value={quickAddData.unit}
                          onChange={event => setQuickAddData(prev => ({ ...prev, unit: event.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                        >
                          <option value="pieces">Pieces</option>
                          <option value="meters">Meters</option>
                          <option value="yards">Yards</option>
                          <option value="spools">Spools</option>
                          <option value="kg">Kilograms</option>
                          <option value="grams">Grams</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          value={quickAddData.category}
                          onChange={event => setQuickAddData(prev => ({ ...prev, category: event.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                        >
                          <option value="">Select category...</option>
                          <option value="fabric">Fabric</option>
                          <option value="thread">Thread</option>
                          <option value="buttons">Buttons</option>
                          <option value="zipper">Zipper</option>
                          <option value="accessories">Accessories</option>
                          <option value="tools">Tools</option>
                          <option value="machines">Machines</option>
                        </select>
                    </div>
                  </div>
              
                    <button
                      type="button"
                      onClick={() => setShowAdvancedFields(prev => !prev)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {showAdvancedFields ? 'Hide additional details' : 'More details'}
                    </button>

                    {showAdvancedFields ? (
                      <div className="space-y-4 border border-gray-200 rounded-xl p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              rows={3}
                              value={advancedData.description}
                              onChange={event => setAdvancedData(prev => ({ ...prev, description: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white text-gray-900"
                              placeholder="Notes about texture, use, or finish"
                            />
                    </div>
                    <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                              <input
                                type="text"
                                value={advancedData.sku}
                                onChange={event => setAdvancedData(prev => ({ ...prev, sku: event.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                                placeholder="Optional code"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum stock</label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={advancedData.minimum_stock}
                                onChange={event => setAdvancedData(prev => ({ ...prev, minimum_stock: event.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                            <select
                              value={advancedData.supplier_id}
                              onChange={event => setAdvancedData(prev => ({ ...prev, supplier_id: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                            >
                              <option value="">No supplier</option>
                              {suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.id}>
                                  {supplier.company_name}
                                </option>
                              ))}
                            </select>
                        </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                            <input
                              type="text"
                              value={advancedData.subcategory}
                              onChange={event => setAdvancedData(prev => ({ ...prev, subcategory: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                              placeholder="e.g., Cotton, Silk, Synthetic"
                            />
                      </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Maximum stock</label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={advancedData.maximum_stock}
                              onChange={event => setAdvancedData(prev => ({ ...prev, maximum_stock: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cost per unit</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={advancedData.cost_per_unit}
                              onChange={event => setAdvancedData(prev => ({ ...prev, cost_per_unit: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Selling price</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={advancedData.selling_price}
                              onChange={event => setAdvancedData(prev => ({ ...prev, selling_price: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                            <input
                              type="text"
                              value={advancedData.color}
                              onChange={event => setAdvancedData(prev => ({ ...prev, color: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                              placeholder="Optional"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                              rows={2}
                              value={advancedData.notes}
                              onChange={event => setAdvancedData(prev => ({ ...prev, notes: event.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white text-gray-900"
                              placeholder="Any reminders for this item"
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {modalError ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-600 mb-3">{modalError}</p>
                        {showUpgradePrompt && (
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              Upgrade to Premium to unlock inventory management and other advanced features.
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                closeQuickAddModal();
                                navigate('/pricing');
                              }}
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Upgrade Now
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null}
                    </form>
                  </div>

                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                          <button 
                        type="button"
                        onClick={closeQuickAddModal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        disabled={isInventorySubmitting}
                      >
                        Cancel
                          </button>
                      <button
                        type="submit"
                        form="quickAddForm"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        disabled={isInventorySubmitting}
                      >
                        {isInventorySubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            Saving
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            {modalMode === 'edit' ? 'Save changes' : 'Create item'}
                          </>
                        )}
                      </button>
                    </div>
                </motion.div>
              </div>
            ) : null}

            {/* Detail Sidebar */}
            {isDetailOpen && selectedItem ? (
              <div 
                className="fixed inset-0 z-40 flex justify-end bg-black/20"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsDetailOpen(false);
                    setSelectedItem(null);
                  }
                }}
              >
                <motion.aside
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col"
                >
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{selectedItem.name}</h2>
                      <p className="text-sm text-gray-500">
                        {'Last updated ' + new Date(selectedItem.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsDetailOpen(false);
                        setSelectedItem(null);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                    <section>
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                        At a glance
                      </h3>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p>
                          Quantity{' '}
                          <span className="font-medium text-gray-900">
                            {selectedItem.current_stock + ' ' + selectedItem.unit}
                          </span>
                        </p>
                        <p>Category: {selectedItem.category || 'Uncategorized'}</p>
                        {selectedItem.subcategory ? <p>Subcategory: {selectedItem.subcategory}</p> : null}
                        <p>Minimum stock: {selectedItem.minimum_stock}</p>
                        {selectedItem.maximum_stock ? <p>Maximum stock: {selectedItem.maximum_stock}</p> : null}
                        <p>Total value: {formatCurrency(selectedItem.total_value)}</p>
                      </div>
                    </section>

                    <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                          Supplier
                        </h3>
                        <div className="space-y-2 text-sm text-gray-600">
                          <p>Supplier: {selectedItem.supplier && selectedItem.supplier.company_name ? selectedItem.supplier.company_name : 'Not assigned'}</p>
                        </div>
                    </section>

                    {selectedItem.notes ? (
                      <section>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                          Notes
                        </h3>
                        <p className="text-sm text-gray-600">{selectedItem.notes}</p>
                      </section>
                    ) : null}
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                    {selectedItem.is_active !== false ? (
                      <button
                        onClick={() => handleArchiveItem(selectedItem.id)}
                        className="inline-flex items-center gap-2 text-sm text-orange-700 hover:text-orange-800"
                        disabled={isInventorySubmitting}
                      >
                        <Archive className="w-4 h-4" />
                        Archive
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnarchiveItem(selectedItem.id)}
                        className="inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-800"
                        disabled={isInventorySubmitting}
                      >
                        <Archive className="w-4 h-4" />
                        Unarchive
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteItemPermanent(selectedItem.id)}
                      className="inline-flex items-center gap-2 text-sm text-red-700 hover:text-red-800"
                      disabled={isInventorySubmitting}
                    >
                      <Trash className="w-4 h-4" />
                      Delete
                    </button>
                    <button
                      onClick={() => {
                        openEditModal(selectedItem);
                        setIsDetailOpen(false);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Edit in full form
                      </button>
                    </div>
                </motion.aside>
                </div>
            ) : null}
          </div>
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard 
            user={user}
            onNavigateToOrders={() => navigate('/business/orders')}
            onNavigateToOverview={() => setActiveTab('overview')}
          />
        )}

        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Profile Management</h2>
                <p className="text-gray-600 mt-1">Manage your business profile and account settings</p>
              </div>
            </div>

            {/* Dashboard View Toggle */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dashboard View Settings</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">View Style</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose between classic or enhanced dashboard view
                  </p>
                </div>
                <button
                  onClick={toggleDashboardView}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {dashboardView === 'classic' ? (
                    <ToggleLeft className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ToggleRight className="w-5 h-5 text-blue-600" />
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {dashboardView === 'classic' ? 'Classic' : 'Enhanced'}
                  </span>
                </button>
              </div>
            </div>

            {/* Profile Completion */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <ProfileCompletion 
                userRole="tailor"
                onStepClick={handleProfileStepClick}
                showDetailed={true}
                compact={false}
              />
            </div>

          </motion.div>
        )}
  </main>

      {/* Guided Tour */}
      {showGuidedSetup && (
        <GuidedTour role="tailor" open={showGuidedSetup} onClose={() => { setShowGuidedSetup(false); try { localStorage.setItem('tour.tailor.shown','1'); } catch (_) {} }} />
      )}

      {/* Tier Upgrade Modal */}
      <TierUpgradeModalSimple
        isOpen={showTierUpgrade}
        onClose={() => setShowTierUpgrade(false)}
        currentTier={userTier}
        upgradeReason={upgradeReason}
      />

      {/* Customer Creation Modal */}
      <AddCustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => {
          setIsCustomerModalOpen(false);
          setCustomerModalMode('select');
          setCustomerFormData({ 
            name: '', 
            email: '', 
            phone: '', 
            address: {
              address: '',
              city: '',
              state: '',
              postalCode: '',
              country: '',
              coordinates: undefined
            }
          });
          setSelectedCountryCode('+91');
          setLinkingData({ linkUrl: '', qrCodeData: '', qrCodeImage: '', expiresAt: '', isGenerating: false });
        }}
        onCustomerCreated={async (customer: Customer) => {
          // Add customer to local state
          const normalizedCustomer: Customer = {
            id: customer.id,
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
            orderCount: 0,
            lastOrderDate: null,
            isActive: true,
            is_verified_via_link: false, // New customers are not verified yet
            linked_at: null
          };

          let customerAlreadyExists = false;
          setCustomers((prev) => {
            const hasExisting = prev.some((c) => c.id === normalizedCustomer.id);
            customerAlreadyExists = hasExisting;
            if (hasExisting) {
              return prev.map((c) =>
                c.id === normalizedCustomer.id ? { ...c, ...normalizedCustomer } : c
              );
            }
            return [normalizedCustomer, ...prev];
          });

          if (!customerAlreadyExists) {
            setCurrentCounts((prev) => ({
              ...prev,
              customers: prev.customers + 1,
            }));
          }

          // Refresh customers list
          await refreshInventoryData();
          
          setCustomerSuccessMessage('Customer created and invitation sent successfully!');
          setTimeout(() => setCustomerSuccessMessage(''), 5000);
        }}
        mode="standalone"
      />

      {/* Customer Detail Modal */}
      {isCustomerDetailOpen && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-semibold text-lg">
                      {selectedCustomer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedCustomer.name}</h2>
                    <p className="text-sm text-gray-600">{selectedCustomer.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsCustomerDetailOpen(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-6">
                {/* Customer Status */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Customer Status</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {selectedCustomer.orderCount > 0 ? (
                        <>
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-green-700 font-medium">Active Customer</span>
                        </>
                      ) : (
                        <>
                          <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                          <span className="text-orange-700 font-medium">Pending Signup</span>
                        </>
                      )}
                    </div>
                    <span className="text-sm text-gray-600">
                      {selectedCustomer.orderCount} {selectedCustomer.orderCount === 1 ? 'order' : 'orders'}
                    </span>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-gray-700">{selectedCustomer.email}</span>
                    </div>
                    {selectedCustomer.phone && (
                      <div className="flex items-center">
                        <span className="w-4 h-4 mr-3">📞</span>
                        <span className="text-gray-700">{selectedCustomer.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order History */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Order History</h3>
                  {selectedCustomer.orderCount > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">Total Orders</p>
                          <p className="text-sm text-gray-600">{selectedCustomer.orderCount} orders placed</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            Last order: {selectedCustomer.lastOrderDate ? new Date(selectedCustomer.lastOrderDate).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-orange-50 rounded-lg">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Package className="w-6 h-6 text-orange-600" />
                      </div>
                      <p className="text-orange-700 font-medium">No orders yet</p>
                      <p className="text-sm text-orange-600">Customer hasn't placed any orders</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedCustomer.orderCount === 0 ? (
                      <button
                        onClick={() => handleResendInvitation(selectedCustomer)}
                        className="flex items-center justify-center px-4 py-3 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Invitation
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSendReminder(selectedCustomer)}
                        className="flex items-center justify-center px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Reminder
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/business/orders/new')}
                      className="flex items-center justify-center px-4 py-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Order
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsCustomerDetailOpen(false);
                    setSelectedCustomer(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {isArchiveModalOpen && itemToArchive && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsArchiveModalOpen(false);
              setItemToArchive(null);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-md bg-white rounded-2xl shadow-xl"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Archive Item</h2>
                <p className="text-sm text-gray-600">This action will remove the item from your active inventory</p>
              </div>
              <button 
                onClick={() => {
                  setIsArchiveModalOpen(false);
                  setItemToArchive(null);
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-orange-800">Are you sure?</h3>
                    <p className="text-sm text-orange-700 mt-1">
                      You're about to archive <strong>"{itemToArchive.name}"</strong>. This will remove it from your active inventory, but you can restore it later if needed.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p><strong>Item:</strong> {itemToArchive.name}</p>
                <p><strong>Current Stock:</strong> {itemToArchive.current_stock} {itemToArchive.unit}</p>
                <p><strong>Category:</strong> {itemToArchive.category}</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button 
                type="button"
                onClick={() => {
                  setIsArchiveModalOpen(false);
                  setItemToArchive(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isInventorySubmitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchiveItem}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                disabled={isInventorySubmitting}
              >
                {isInventorySubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4" />
                    Archive Item
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsDeleteModalOpen(false);
              setItemToDelete(null);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Delete Item</h2>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
              <button 
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setItemToDelete(null);
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Warning: Permanent Deletion</h3>
                    <p className="text-sm text-red-700 mt-1">
                      You're about to permanently delete <strong>"{itemToDelete.name}"</strong>. This action cannot be undone and will remove all data associated with this item.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p><strong>Item:</strong> {itemToDelete.name}</p>
                <p><strong>Current Stock:</strong> {itemToDelete.current_stock} {itemToDelete.unit}</p>
                <p><strong>Category:</strong> {itemToDelete.category}</p>
                <p className="text-red-600 mt-2"><strong>⚠️ This will permanently remove all data for this item.</strong></p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button 
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setItemToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isInventorySubmitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteItem}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                disabled={isInventorySubmitting}
              >
                {isInventorySubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash className="w-4 h-4" />
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Payment Methods Modal */}
      <PaymentMethodsModal
        isOpen={showPaymentMethods}
        onClose={() => setShowPaymentMethods(false)}
      />
    </div>
  );
};

export default BusinessDashboard;


